/**
 * Typed fetch wrappers for the timecore backend.
 *
 * Base URL is read from the VITE_TIMECORE_URL env var (set in .env),
 * falling back to localhost:4000 for local development.
 */
// autoReconnectWs removed - no longer needed after migrating tickets to wormhole
import { CapacitorHttp } from '@capacitor/core';
import type { RecordStatus, SourceServer, TicketRecord } from '@timehuddle/ticket-sources';
import type { DetailedError } from 'tus-js-client';

import { getDdpClient } from './ddp.js';
import { toLinkedIssue, type LinkedIssue } from './ticketLink.js';

// ─── Config ───────────────────────────────────────────────────────────────────

export const TIMECORE_BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_TIMECORE_URL) ||
  'http://localhost:4000';

const _WS_BASE_URL = TIMECORE_BASE_URL.replace(/^http/, 'ws');

/** Meteor backend (wormhole REST + DDP) base URL — direct URL for assets, OAuth, etc. */
export const METEOR_BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_TIMECORE_URL) ||
  'http://localhost:3100';

/**
 * Base URL for REST API fetch calls (wormhole, avatar, media, etc.).
 *
 * - Capacitor native: no Vite proxy, must use absolute VITE_TIMECORE_URL.
 * - Web with explicit VITE_TIMECORE_URL (Docker/production preview): frontend
 *   and backend are on different hostnames, so use the absolute backend URL.
 * - Web without VITE_TIMECORE_URL (local dev): Vite proxy handles relative
 *   paths, so use '' (same-origin).
 */
const _isNativeWebView =
  typeof window !== 'undefined' &&
  typeof (window as unknown as Record<string, unknown>).Capacitor !== 'undefined' &&
  (
    window as unknown as { Capacitor: { isNativePlatform: () => boolean } }
  ).Capacitor.isNativePlatform();

const _hasExplicitApiUrl = !!(
  typeof import.meta !== 'undefined' &&
  (import.meta as { env?: Record<string, string> }).env?.VITE_TIMECORE_URL
);

export const METEOR_API_BASE: string =
  _isNativeWebView || _hasExplicitApiUrl ? METEOR_BASE_URL : '';

const FORCED_TIMEZONE: string | undefined =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_FORCE_TIMEZONE?.trim()) ||
  undefined;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimecoreUser {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  emailVerified: boolean;
  image?: string | null;
  backgroundUrl?: string | null;
  /** Canonical username — null until the user has claimed one. */
  username: string | null;
  /**
   * Newest release note this user has read, or null if they never have.
   * Drives the "New" flags on /app/release-notes and the menu badge.
   */
  releaseNotesSeenVersion: string | null;
  organizationMembership?: {
    organizationId: string;
    organizationSlug: string;
    role: 'owner' | 'admin';
  } | null;
  organizations?: Array<{
    id: string;
    name: string;
    slug: string;
    enterpriseId: string | null;
    role: 'owner' | 'admin' | 'member';
    allowAutoJoin: boolean;
  }>;
}

/** The caller's linked GitHub account (`github.status`). */
export interface GitHubLinkStatus {
  connected: boolean;
  username: string | null;
}

export interface PublicUser {
  id: string;
  name: string;
  /** Canonical username/handle. Null until the user claims one. */
  username: string | null;
  image: string | null;
  backgroundUrl: string | null;
  bio: string;
  website: string;
  reportsTo: { id: string; name: string; username: string | null } | null;
  teamMemberships: Array<{ id: string; name: string; role: 'admin' | 'member' }>;
  /** Teams shared between the viewer and this user (non-personal). Empty for own profile. */
  sharedTeams?: Array<{ id: string; name: string; isAdmin: boolean }>;
}

/**
 * Backend-served media paths. Anything under these is addressed by path only —
 * whichever host is serving the backend right now owns it.
 */
export const MEDIA_PATH_PREFIXES = ['/uploads/', '/pulsevault/'];

/**
 * Resolve a media URL against the *current* backend origin.
 *
 * Media URLs were historically persisted absolute (baked from ROOT_URL /
 * VITE_TIMECORE_URL at upload time), which breaks the moment the backend moves
 * — most visibly in dev, where the stack is served from the machine's LAN IP
 * and every DHCP lease change orphans every previously-posted image and video.
 *
 * So the host in a stored media URL is treated as advisory: any URL whose path
 * is backend-owned ({@link MEDIA_PATH_PREFIXES}) is re-based onto the origin
 * this session actually talks to, whether it arrived relative or
 * absolute-with-a-stale-host. URLs pointing anywhere else (a real CDN, an
 * external link) are left alone.
 *
 * Re-based onto METEOR_API_BASE, not METEOR_BASE_URL: on proxied web dev that
 * is '' — a same-origin path served through Vite's /uploads and /pulsevault
 * proxies, which works from any client on the network, not just the machine
 * running the backend. Native and explicit-URL builds get the absolute backend
 * URL, as before.
 */
export function resolveMediaUrl(url: string | undefined | null): string {
  if (!url) return '';
  let path = url;
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (!MEDIA_PATH_PREFIXES.some((p) => parsed.pathname.startsWith(p))) return url;
      path = `${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }
  return `${METEOR_API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

function toAbsoluteUrl(url: string | null): string | null {
  if (!url) return url;
  const isMediaPath = MEDIA_PATH_PREFIXES.some((p) => url.startsWith(p));
  if (isMediaPath || /^https?:\/\//i.test(url)) return resolveMediaUrl(url);
  return `${TIMECORE_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

function withAbsoluteImage(user: PublicUser): PublicUser {
  const image = toAbsoluteUrl(user.image);
  const backgroundUrl = toAbsoluteUrl(user.backgroundUrl);
  if (image === user.image && backgroundUrl === user.backgroundUrl) return user;
  return { ...user, image, backgroundUrl };
}

/** API error that carries the HTTP status code. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Meteor.Error code (e.g. 'plan-required'), when the backend provided one. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Token storage (for Capacitor / custom-scheme WebViews where cookies are unreliable) ──

const TOKEN_KEY = 'timecore_session_token';

export const sessionToken = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

// ─── JWT access token (stateless auth for the Meteor backend) ──────────────────────

let cachedJwt: { token: string; exp: number } | null = null;
let jwtFetch: Promise<string | null> | null = null;

/** Decode the `exp` claim (seconds since epoch) from a JWT, or 0 on failure. */
export function decodeJwtExp(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : 0;
  } catch {
    return 0;
  }
}

/**
 * Identifies who is signed in right now: changes on sign-out and when someone
 * else signs in. Work queued for later (e.g. preference saves) compares it so
 * one person's change is never sent with the next person's credentials.
 */
export function currentSessionKey(): string | null {
  try {
    return localStorage.getItem('meteor_resume_token');
  } catch {
    return null;
  }
}

/**
 * Get a short-lived JWT access token from better-auth (`GET /api/auth/token`),
 * cached until ~60s before expiry. Falls back to Meteor resume token when no
 * Fastify session is available. Returns null when signed out.
 */
export async function getAccessToken(): Promise<string | null> {
  // First try cached JWT (still valid for Fastify sessions)
  if (cachedJwt && cachedJwt.exp * 1000 - Date.now() > 60_000) return cachedJwt.token;

  // Fall back to Meteor resume token for Meteor-authenticated users
  const meteorToken = localStorage.getItem('meteor_resume_token');
  if (meteorToken) return meteorToken;

  // Try Fastify JWT if we have a session token
  const session = sessionToken.get();
  if (!session) return null;

  jwtFetch ??= (async () => {
    try {
      const res = await fetch(`${TIMECORE_BASE_URL}/api/auth/token`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${session}` },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string };
      if (!data.token) return null;
      cachedJwt = { token: data.token, exp: decodeJwtExp(data.token) };
      return data.token;
    } catch {
      return null;
    } finally {
      jwtFetch = null;
    }
  })();
  return jwtFetch;
}

/** Drop the cached JWT (on sign-out). */
export function clearAccessToken(): void {
  cachedJwt = null;
}

// ─── Base request ─────────────────────────────────────────────────────────────

async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const hasBody = options.body != null;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const token = await getAccessToken();
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () =>
      controller.abort(new Error('Request timed out. Please check your connection and try again.')),
    8000,
  );
  // overwrite the merged headers object (which would drop Authorization).
  const { headers: optHeaders, ...restOptions } = options;
  try {
    const res = await fetch(`${TIMECORE_BASE_URL}${path}`, {
      credentials: 'include',
      signal: controller.signal,
      headers: {
        ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...optHeaders,
      },
      ...restOptions,
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError(
        (body.message as string | undefined) ??
          (body.error as string | undefined) ??
          `HTTP ${res.status}`,
        res.status,
      );
    }

    return res.json() as Promise<T>;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ─── Auth API ─────────────────────────────────────────────────────────────────

export const authApi = {
  /**
   * Initiate a social OAuth sign-in (GitHub / Google / Apple).
   * Returns the provider redirect URL; caller should set window.location.href to it.
   */
  signInWithSocial: async (
    provider: 'github' | 'google' | 'apple',
    callbackURL: string,
  ): Promise<string> => {
    const res = await fetch(`${TIMECORE_BASE_URL}/api/auth/sign-in/social`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, callbackURL }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new Error(
        (body.message as string | undefined) ??
          (body.error as string | undefined) ??
          `HTTP ${res.status}`,
      );
    }
    const data = (await res.json()) as { url: string };
    return data.url;
  },

  /**
   * Initiate a generic OAuth2 / OIDC sign-in (e.g. Authentik) via the
   * better-auth `genericOAuth` plugin. Returns the provider redirect URL.
   */
  signInWithOAuth2: async (providerId: string, callbackURL: string): Promise<string> => {
    const res = await fetch(`${TIMECORE_BASE_URL}/api/auth/sign-in/oauth2`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerId, callbackURL }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new Error(
        (body.message as string | undefined) ??
          (body.error as string | undefined) ??
          `HTTP ${res.status}`,
      );
    }
    const data = (await res.json()) as { url: string };
    return data.url;
  },

  /** Sign out — clears better-auth session cookie and stored token. */
  signOut: async () => {
    await request('/api/auth/sign-out', { method: 'POST' }).catch(() => {});
    sessionToken.clear();
    clearAccessToken();
  },

  /**
   * Fetch the currently authenticated user.
   * Returns null on 401 (not authenticated), throws on other errors.
   */
  getMe: async (): Promise<{ user: TimecoreUser } | null> => {
    try {
      const data = await request<{ user: TimecoreUser }>('/v1/me');
      if (data?.user?.image && !/^https?:\/\//i.test(data.user.image)) {
        data.user.image = `${TIMECORE_BASE_URL}${data.user.image.startsWith('/') ? '' : '/'}${data.user.image}`;
      }
      if (data?.user?.backgroundUrl && !/^https?:\/\//i.test(data.user.backgroundUrl)) {
        data.user.backgroundUrl = `${TIMECORE_BASE_URL}${data.user.backgroundUrl.startsWith('/') ? '' : '/'}${data.user.backgroundUrl}`;
      }
      return data;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
};

// ─── User API ─────────────────────────────────────────────────────────────────

export const userApi = {
  uploadAvatar: async (blob: Blob): Promise<{ avatarUrl: string }> => {
    const formData = new FormData();
    formData.append('avatar', blob, 'avatar.png');
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/me/avatar`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError((body.error as string) ?? `HTTP ${res.status}`, res.status);
    }
    const data = (await res.json()) as { avatarUrl: string };
    return { avatarUrl: toAbsoluteUrl(data.avatarUrl) as string };
  },

  deleteAvatar: async (): Promise<void> => {
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/me/avatar`, {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status);
  },

  uploadBackground: async (blob: Blob): Promise<{ backgroundUrl: string }> => {
    const formData = new FormData();
    formData.append('background', blob, 'background.jpg');
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/me/background`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError((body.error as string) ?? `HTTP ${res.status}`, res.status);
    }
    const data = (await res.json()) as { backgroundUrl: string };
    return { backgroundUrl: toAbsoluteUrl(data.backgroundUrl) as string };
  },

  deleteBackground: async (): Promise<void> => {
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/me/background`, {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status);
  },
  getUser: (id: string) =>
    wormholeCall<{ user: PublicUser }>('users.get', { userId: id }).then((r) =>
      withAbsoluteImage(r.user),
    ),

  getUserByUsername: (username: string) =>
    wormholeCall<{ user: PublicUser }>('users.getByUsername', { username }).then((r) =>
      withAbsoluteImage(r.user),
    ),

  getUsers: (ids: string[]) =>
    wormholeCall<{ users: PublicUser[] }>('users.batchGet', { ids }).then((r) =>
      r.users.map(withAbsoluteImage),
    ),

  updateProfile: (data: {
    name?: string;
    image?: string | null;
    bio?: string;
    website?: string;
    reportsToUserId?: string | null;
  }) => wormholeCall<{ user: PublicUser }>('users.updateProfile', data).then((r) => r.user),

  /** Permanently delete the current user's account and all associated data. */
  deleteAccount: async (): Promise<void> => {
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/me/account`, {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError((body.error as string) ?? `HTTP ${res.status}`, res.status);
    }
  },
};

export type DefaultOrganizationRole = 'owner' | 'admin' | 'member';

export interface OrganizationAdminUser {
  id: string;
  name: string;
  email: string;
  username: string | null;
  image: string | null;
  role: DefaultOrganizationRole;
  reportsToUserId?: string | null;
  blocked?: Array<{ orgId: string; blockedBy: string; blockedAt: string; reason?: string }>;
}

export interface AdminOrganization {
  id: string;
  key: string;
  name: string;
  ownersCount?: number;
  adminsCount?: number;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked' | 'delivery_failed';
  invitedByName: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
  revokedAt?: string;
}

export const orgAdminApi = {
  getOrganization: () =>
    wormholeCall<{ organization: AdminOrganization }>('orgs.adminGet', {}).then(
      (r) => r.organization,
    ),

  updateOrganizationName: (name: string) =>
    wormholeCall<{ organization: AdminOrganization }>('orgs.adminUpdate', { name }).then(
      (r) => r.organization,
    ),

  listUsers: () =>
    wormholeCall<{ users: OrganizationAdminUser[] }>('orgs.adminListUsers', {}).then(
      (r) => r.users,
    ),

  setUserRole: (userId: string, role: DefaultOrganizationRole) =>
    wormholeCall<{ user: { id: string; role: DefaultOrganizationRole } }>('orgs.adminSetUserRole', {
      userId,
      role,
    }).then((r) => r.user),

  updateReportsTo: (userId: string, reportsTo: string | null) =>
    wormholeCall<{ user: { id: string; reportsToUserId: string | null } }>('orgs.updateReportsTo', {
      userId,
      reportsToUserId: reportsTo,
    }).then((r) => r.user),
};

// ─── Org Usage API (default-org owners and admins) ───────────────────────────

/** Feature buckets the usage report counts actions into. */
export const USAGE_FEATURES = [
  { key: 'clock', label: 'Clock' },
  { key: 'posts', label: 'Huddle posts' },
  { key: 'comments', label: 'Comments' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'timers', label: 'Work timers' },
  { key: 'pulse', label: 'Pulse videos' },
] as const;

export type UsageFeature = (typeof USAGE_FEATURES)[number]['key'];

/** How habitually a member uses TimeHuddle, over the report's 30-day window. */
export type UsageCadence = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'dormant';

/** Whether the member did anything in the selected period — not their cadence. */
export type UsageStatus = 'active' | 'idle' | 'blocked';

/** Periods the counts can be scoped to. Must match USAGE_PERIOD_DAYS on the server. */
export const USAGE_PERIOD_DAYS = [1, 7, 14, 30] as const;

export type UsagePeriodDays = (typeof USAGE_PERIOD_DAYS)[number];

export interface UsageOrganization {
  id: string;
  name: string;
}

export interface OrgUsageUser {
  id: string;
  name: string;
  email: string;
  username: string | null;
  image: string | null;
  /** Highest role they hold across the organizations in scope. */
  role: DefaultOrganizationRole | 'member';
  blocked: boolean;
  /** Those in scope they belong to — more than one only in the all-orgs view. */
  organizations: UsageOrganization[];
  status: UsageStatus;
  /** Actions per feature within the selected period. */
  features: Record<UsageFeature, number>;
  totalActions: number;
  /** Distinct days with at least one action, within the selected period. */
  activeDays: number;
  cadence: UsageCadence;
  /** Distinct active days across the full cadence window, whatever the period. */
  cadenceActiveDays: number;
  topFeature: UsageFeature | null;
  lastActiveAt: string | null;
}

export interface OrgUsageReport {
  /** Every organization the caller may report on — drives the picker. */
  availableOrganizations: UsageOrganization[];
  /** Those this report actually covers: one, or all of them. */
  organizations: UsageOrganization[];
  /** null when the report spans every organization the caller administers. */
  orgId: string | null;
  periodDays: UsagePeriodDays;
  cadenceWindowDays: number;
  timezone: string;
  generatedAt: string;
  totals: {
    members: number;
    activeMembers: number;
    cadenceCounts: Record<UsageCadence, number>;
    featureTotals: Record<UsageFeature, number>;
    topFeature: UsageFeature | null;
  };
  users: OrgUsageUser[];
}

export const usageApi = {
  /**
   * Per-member usage for the organizations the caller owns or administers.
   * Pass an `orgId` to narrow to one; omit it to span all of them. Day buckets
   * are cut in the viewer's own timezone, so "today" means their today.
   */
  getOrgUsage: (periodDays: UsagePeriodDays, orgId?: string) =>
    wormholeCall<OrgUsageReport>('usage.orgUsage', {
      periodDays,
      timezone: clientTz(),
      ...(orgId ? { orgId } : {}),
    }),
};

// ─── Public Organization API (for all authenticated users) ──────────────────

/**
 * A server an org has registered, so an issue on `host` shows as `PM-233`
 * (#689), read by its type's definition (#734 step 5).
 */
export type SourcePrefix = SourceServer;

export const orgApi = {
  checkSlugAvailability: (slug: string, excludeId?: string) =>
    wormholeCall<{ available: boolean }>('orgs.checkSlug', {
      slug,
      ...(excludeId ? { excludeId } : {}),
    }).then((r) => r.available),

  listOrganizations: () =>
    wormholeCall<{
      organizations: Array<{
        id: string;
        enterpriseId: string | null;
        /** Shown to every member, unlike `enterprises.list`. */
        enterpriseName: string | null;
        name: string;
        slug: string;
        allowAutoJoin: boolean;
        role: 'owner' | 'admin' | 'member' | null;
        sourcePrefixes?: SourcePrefix[];
      }>;
    }>('orgs.list', {}).then((r) => r.organizations),

  createOrganization: (data: {
    enterpriseId: string;
    name: string;
    slug?: string;
    allowAutoJoin?: boolean;
  }) =>
    wormholeCall<{
      organization: {
        id: string;
        enterpriseId: string | null;
        name: string;
        slug: string;
        allowAutoJoin: boolean;
        role: 'owner' | 'admin' | 'member' | null;
      };
    }>('orgs.create', data).then((r) => r.organization),

  updateOrganization: (
    id: string,
    data: { name?: string; slug?: string; allowAutoJoin?: boolean },
  ) =>
    wormholeCall<{
      organization: {
        id: string;
        enterpriseId: string | null;
        name: string;
        slug: string;
        allowAutoJoin: boolean;
        role: 'owner' | 'admin' | 'member' | null;
      };
    }>('orgs.update', { orgId: id, ...data }).then((r) => r.organization),

  getOrganizationById: (id: string) =>
    wormholeCall<{
      organization: {
        id: string;
        enterpriseId: string | null;
        name: string;
        slug: string;
        allowAutoJoin: boolean;
        ticketPrefix: string;
        sourcePrefixes: SourcePrefix[];
        role: 'owner' | 'admin' | 'member' | null;
        canManage: boolean;
        /** An org owner by membership; only they may change ticket IDs. */
        canEditTicketIds: boolean;
      } & SettingsEditView;
    }>('orgs.get', { orgId: id }).then((r) => r.organization),

  /** Sets the prefix every ticket in the org is shown with. Org owners only. */
  setTicketPrefix: (id: string, ticketPrefix: string) =>
    wormholeCall<{ ticketPrefix: string }>('orgs.setTicketPrefix', {
      orgId: id,
      ticketPrefix,
    }).then((r) => r.ticketPrefix),

  /** Names the servers outside issues live on, replacing the list. Org owners only. */
  setSourcePrefixes: (id: string, sourcePrefixes: SourcePrefix[]) =>
    wormholeCall<{ sourcePrefixes: SourcePrefix[] }>('orgs.setSourcePrefixes', {
      orgId: id,
      sourcePrefixes,
    }).then((r) => r.sourcePrefixes),

  /** Sets or resets default values and locks; `allowAutoJoin` alone is the old call. */
  updateSettings: (id: string, patch: { settings?: SettingsPatch; locks?: SettingsPatch }) =>
    wormholeCall<{ organization: { orgId: string } & SettingsEditView }>('orgs.updateSettings', {
      orgId: id,
      ...patch,
    }).then((r) => r.organization),

  joinOrganization: (id: string) =>
    wormholeCall<{ membership: { orgId: string; role: 'owner' | 'admin' | 'member' } }>(
      'orgs.join',
      { orgId: id },
    ).then((r) => r.membership),

  listMembers: (id: string) =>
    wormholeCall<{ users: OrganizationAdminUser[] }>('orgs.listMembers', { orgId: id }).then(
      (r) => r.users,
    ),

  listOrganizationUsers: (id: string) =>
    wormholeCall<{ users: OrganizationAdminUser[] }>('orgs.listUsers', { orgId: id }).then(
      (r) => r.users,
    ),

  searchUsers: (id: string, q: string) =>
    wormholeCall<{ users: Array<{ id: string; name: string; username: string | null }> }>(
      'orgs.searchUsers',
      { orgId: id, q },
    ).then((r) => r.users),

  setMemberRole: (id: string, userId: string, role: DefaultOrganizationRole) =>
    wormholeCall<{ user: { userId: string; role: DefaultOrganizationRole } }>(
      'orgs.setMemberRole',
      { orgId: id, userId, role },
    ).then((r) => r.user),

  removeMember: (id: string, userId: string) =>
    wormholeCall<{ user: { userId: string } }>('orgs.removeMember', { orgId: id, userId }).then(
      (r) => r.user,
    ),

  inviteMember: (id: string, email: string) =>
    wormholeCall<
      | { ok: true; status: 'joined' }
      | { ok: true; status: 'pending'; invitationId: string; expiresAt: string }
    >('orgs.invite', { orgId: id, email }),

  getPendingInvitations: (id: string) =>
    wormholeCall<{ invitations: OrganizationInvitation[] }>('orgs.getPendingInvitations', {
      orgId: id,
    }).then((r) => r.invitations),

  revokeInvitation: (invitationId: string) =>
    wormholeCall<{ ok: boolean }>('orgs.revokeInvite', { invitationId }),

  blockMember: (id: string, targetUserId: string, reason?: string) =>
    wormholeCall<{
      user: {
        id: string;
        blocked: { orgId: string; blockedBy: string; blockedAt: string; reason?: string };
      };
    }>('orgs.blockMember', { orgId: id, targetUserId, reason }).then((r) => r.user),

  unblockMember: (id: string, targetUserId: string) =>
    wormholeCall<{ user: { id: string } }>('orgs.unblockMember', { orgId: id, targetUserId }).then(
      (r) => r.user,
    ),

  updateMemberReportsTo: (id: string, userId: string, reportsTo: string | null) =>
    wormholeCall<{ user: { id: string; reportsToUserId: string | null } }>(
      'orgs.updateMemberReportsTo',
      { orgId: id, userId, reportsToUserId: reportsTo },
    ).then((r) => r.user),

  updateReportsTo: (userId: string, reportsTo: string | null) =>
    wormholeCall<{ user: { id: string; reportsToUserId: string | null } }>('orgs.updateReportsTo', {
      userId,
      reportsToUserId: reportsTo,
    }).then((r) => r.user),

  getOrganization: () =>
    wormholeCall<{ organization: AdminOrganization }>('orgs.publicGet', {}).then(
      (r) => r.organization,
    ),

  listUsers: () =>
    wormholeCall<{ users: OrganizationAdminUser[] }>('orgs.publicListUsers', {}).then(
      (r) => r.users,
    ),
};

export const enterpriseApi = {
  list: () =>
    wormholeCall<{
      enterprises: Array<{ id: string; name: string; slug: string; role: 'owner' | 'admin' }>;
    }>('enterprises.list', {}).then((r) => r.enterprises),

  create: (data: { name: string; slug?: string }) =>
    wormholeCall<{
      enterprise: {
        id: string;
        name: string;
        slug: string;
        role: 'owner' | 'admin';
        owners: string[];
        admins: string[];
      };
    }>('enterprises.create', data).then((r) => r.enterprise),

  get: (id: string) =>
    wormholeCall<{
      enterprise: {
        id: string;
        name: string;
        slug: string;
        role: 'owner' | 'admin';
        owners: string[];
        admins: string[];
        members: Array<{
          id: string;
          name: string;
          username: string | null;
          role: 'owner' | 'admin';
        }>;
      } & SettingsEditView;
    }>('enterprises.get', { enterpriseId: id }).then((r) => r.enterprise),

  updateSettings: (id: string, patch: { settings?: SettingsPatch; locks?: SettingsPatch }) =>
    wormholeCall<{ enterprise: { id: string } & SettingsEditView }>('enterprises.updateSettings', {
      enterpriseId: id,
      ...patch,
    }).then((r) => r.enterprise),

  updateName: (id: string, name: string) =>
    wormholeCall<{
      enterprise: {
        id: string;
        name: string;
        slug: string;
        role: 'owner' | 'admin';
        owners: string[];
        admins: string[];
        members: Array<{
          id: string;
          name: string;
          username: string | null;
          role: 'owner' | 'admin';
        }>;
      };
    }>('enterprises.updateName', { enterpriseId: id, name }).then((r) => r.enterprise),

  searchUsers: (id: string, q: string) =>
    wormholeCall<{ users: Array<{ id: string; name: string; username: string | null }> }>(
      'enterprises.searchUsers',
      { enterpriseId: id, q },
    ).then((r) => r.users),

  removeMember: (id: string, userId: string) =>
    wormholeCall<{ userId: string }>('enterprises.removeMember', { enterpriseId: id, userId }),

  setMemberRole: (id: string, userId: string, role: 'owner' | 'admin') =>
    wormholeCall<{ user: { userId: string; role: 'owner' | 'admin' } }>(
      'enterprises.setMemberRole',
      { enterpriseId: id, userId, role },
    ).then((r) => r.user),

  getOwnershipStatus: () =>
    wormholeCall<{ hasOwner: boolean; installCompleted: boolean }>('enterprise.installStatus', {}),

  takeOwnership: () => wormholeCall<{ role: 'owner' }>('enterprises.takeOwnership', {}),
};

export type SeedImportPreview = {
  ok: true;
  value: unknown;
};

export type SeedImportError = {
  ok: false;
  error: { type: string; message: string };
};

export const seedImportApi = {
  parse: (yaml: string) =>
    request<SeedImportPreview | SeedImportError>('/v1/seed/import/parse', {
      method: 'POST',
      body: JSON.stringify({ yaml }),
    }),

  import: (yaml: string, orgId?: string) =>
    request<{
      created: {
        enterprises: number;
        organizations: number;
        teams: number;
        users: number;
        tickets: number;
      };
      updated: {
        enterprises: number;
        organizations: number;
        teams: number;
        users: number;
      };
      summary?: string;
    }>('/v1/seed/import', {
      method: 'POST',
      body: JSON.stringify({ yaml, orgId }),
    }),
};

// ─── Username API ─────────────────────────────────────────────────────────────

export const usernameApi = {
  check: (username: string) =>
    wormholeCall<{ available: boolean; reason?: string | null }>('users.checkUsername', {
      username,
    }),

  claim: (username: string) =>
    wormholeCall<{ username: string }>('users.claimUsername', { username }),
};

// ─── Wormhole (Meteor REST) request ──────────────────────────────────────────

/** Longest a wormhole call waits on the DDP login before sending with the token it has. */
const DDP_AUTH_WAIT_MS = 10_000;

/** Rejects after `ms` so a call on a dead socket cannot hold its caller forever. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Timed out')), ms);
  });
  return Promise.race([promise, expired]).finally(() => clearTimeout(timer));
}

/**
 * Call a Meteor method via the wormhole REST bridge (POST /api/<name with
 * dots→underscores>). Auth is a short-lived better-auth JWT in the
 * Authorization header — the Meteor auth bridge verifies it against the
 * IdP's JWKS (no shared-session coupling).
 */
async function wormholeCall<T = unknown>(
  method: string,
  params: Record<string, unknown>,
): Promise<T> {
  const route = method.replace(/\./g, '_');

  // Ensure DDP auth is complete before reading token
  // This guarantees tryResumeLogin has run and updated localStorage
  try {
    await withTimeout(getDdpClient().ensureAuthed(), DDP_AUTH_WAIT_MS);
  } catch {
    // If auth fails or stalls, proceed anyway — getAccessToken will handle it
  }

  const token = await getAccessToken();
  const url = `${METEOR_API_BASE}/api/${route}`;

  console.log(`[wormholeCall] ${method}: fetching ${url}`, { hasToken: !!token });

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let status: number;
  let data: {
    result?: T;
    error?: string;
    reason?: string;
    message?: string;
  };

  // In the Capacitor WebView the app is served from a different origin than
  // the API (dev live-reload host, or capacitor://localhost in production), so
  // a plain cross-origin `fetch` is blocked by WebKit CORS and fails with an
  // opaque "Load failed". Route through the native HTTP bridge, which is not
  // subject to WebView CORS. Web keeps using `fetch` (Vite proxy / same-origin).
  if (_isNativeWebView) {
    let response: { status: number; data: unknown };
    try {
      response = await CapacitorHttp.request({ url, method: 'POST', headers, data: params });
    } catch (httpError) {
      console.error(`[wormholeCall] ${method}: native request failed:`, httpError);
      throw httpError;
    }
    status = response.status;
    data =
      typeof response.data === 'string'
        ? ((() => {
            try {
              return JSON.parse(response.data as string);
            } catch {
              return {};
            }
          })() as typeof data)
        : ((response.data as typeof data) ?? {});
  } else {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
    } catch (fetchError) {
      console.error(`[wormholeCall] ${method}: fetch failed:`, fetchError);
      throw fetchError;
    }
    status = res.status;
    data = (await res.json().catch(() => ({}))) as typeof data;
  }

  console.log(`[wormholeCall] ${method}: got response, status=${status}`);

  if (status < 200 || status >= 300) {
    throw new ApiError(
      data.reason || data.message || `Request failed (${status})`,
      status,
      data.error,
    );
  }
  return data.result as T;
}

// ─── Ticket API ───────────────────────────────────────────────────────────────

export interface Ticket {
  id: string;
  teamId: string;
  title: string;
  description: string | null;
  github: string;
  /**
   * Whether the link stands for the ticket's issue, so the ticket goes by that
   * issue's ID. Null on links saved before the choice existed: those do whenever
   * the link names an issue.
   */
  linkIsIssue: boolean | null;
  status: string;
  priority: string | null;
  createdBy: string;
  assignedTo: string[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** The ticket's number in its organization, shown as `TH-12`. Missing until numbered. */
  number?: number;
  sharedWithTimeharbor?: boolean;
  /** The external issue this ticket is linked to, or null. */
  linkedIssue: LinkedIssue | null;
}

/**
 * Whether a linked ticket can be changed right now. `lock` names who is timing
 * it; while anyone is, its fields and its link are refused server-side
 * (`ticket-locked`). Null when nobody is, or when the ticket is not linked.
 */
export interface TicketLinkStatus {
  lock: { holders: { userId: string; name: string }[]; message: string } | null;
  /**
   * The caller's own closed time on the ticket: logged before it had a link
   * (`unlinkedSeconds`, belongs to no issue), and logged under the issue it is
   * linked to now, split by whether it has reached Redmine.
   */
  myTime: { unlinkedSeconds: number; unsentSeconds: number; sentSeconds: number };
  /** How many teammates have logged time on the ticket; they are told when its link changes. */
  othersWithTime: number;
}

/** Normalize a wormhole ticket payload (raw Mongo doc shape) to the Ticket interface. */
function toTicket(raw: Record<string, unknown>): Ticket {
  const assignedTo = raw.assignedTo;
  return {
    id: String(raw.id ?? raw._id),
    teamId: String(raw.teamId),
    title: String(raw.title),
    description: (raw.description as string | undefined) ?? null,
    github: (raw.github as string | undefined) ?? '',
    linkIsIssue: typeof raw.linkIsIssue === 'boolean' ? raw.linkIsIssue : null,
    status: (raw.status as string | undefined) ?? 'open',
    priority: (raw.priority as string | undefined) ?? null,
    createdBy: String(raw.createdBy),
    assignedTo: Array.isArray(assignedTo)
      ? assignedTo.map(String)
      : typeof assignedTo === 'string'
        ? [assignedTo]
        : [],
    reviewedBy: (raw.reviewedBy as string | undefined) ?? null,
    reviewedAt: (raw.reviewedAt as string | undefined) ?? null,
    createdAt: String(raw.createdAt),
    updatedAt: (raw.updatedAt as string | undefined) ?? null,
    number: typeof raw.number === 'number' ? raw.number : undefined,
    sharedWithTimeharbor: raw.sharedWithTimeharbor as boolean | undefined,
    linkedIssue: toLinkedIssue(raw.linkedIssue),
  };
}

export interface TicketDashboardSummary {
  open: number;
  unassignedOpen: number;
  closedToday: number;
  highPriorityOpen: number;
  myOpen: number;
  myClosedToday: number;
  myHighPriorityOpen: number;
}

/** How `tickets.search` read a query. */
export type TicketSearchKind = 'id' | 'url' | 'assignee' | 'text';

/** The outside issue a ticket carries, read through the driver engine (#757). */
export interface OutsideIssue {
  kind: string;
  record: TicketRecord;
  /** The statuses the caller may move it to, current first. */
  choices: { status: RecordStatus[] };
  /** The caller's id in the source, to tell their own activity apart. */
  me: string | null;
}

/** Which issue `issue` is, as `tickets.writeOutside` checks it: `kind:server:key`. */
export function outsideIssueKey(issue: OutsideIssue): string {
  const ref = issue.record.ref;
  return `${issue.kind}:${ref?.server ?? ''}:${ref?.key ?? ''}`;
}

/** Edits to an outside issue, by record field; ids as the source gives them. */
export type OutsideEdits = Partial<{
  title: string;
  body: string;
  status: string;
  priority: string;
  assignees: string[];
}>;

export const ticketApi = {
  /**
   * `brief` leaves out descriptions; `assignedTo` + `activeOnly` narrow the list to
   * one person's tickets that are not closed, reviewed or deleted.
   */
  getTickets: (
    teamId: string,
    options: { brief?: boolean; assignedTo?: string; activeOnly?: boolean } = {},
  ) =>
    wormholeCall<Array<Record<string, unknown>>>('tickets.list', {
      teamId,
      ...(options.brief ? { brief: true } : {}),
      ...(options.assignedTo ? { assignedTo: options.assignedTo } : {}),
      ...(options.activeOnly ? { activeOnly: true } : {}),
    }).then((tickets) => tickets.map(toTicket)),

  /** Dashboard counts, computed server-side. `since`/`until` bound "today" (ms epoch). */
  getDashboardSummary: (teamId: string, since: number, until: number) =>
    wormholeCall<TicketDashboardSummary>('tickets.dashboardSummary', { teamId, since, until }),

  getTicket: (id: string) =>
    wormholeCall<Record<string, unknown>>('tickets.get', { ticketId: id }).then(toTicket),

  /** Create a ticket. `assignedToUserIds` defaults to the creator when omitted. */
  createTicket: (data: {
    teamId: string;
    title: string;
    github?: string;
    linkIsIssue?: boolean;
    description?: string;
    priority?: string;
    assignedToUserIds?: string[];
  }) => wormholeCall<Record<string, unknown>>('tickets.create', data).then(toTicket),

  updateTicket: (
    id: string,
    updates: { title?: string; github?: string; linkIsIssue?: boolean; description?: string },
  ) =>
    wormholeCall<Record<string, unknown>>('tickets.update', { ticketId: id, ...updates }).then(
      toTicket,
    ),

  updateStatusPriority: (id: string, updates: { status?: string; priority?: string }) =>
    wormholeCall<Record<string, unknown>>('tickets.updateStatus', {
      ticketId: id,
      ...updates,
    }).then(toTicket),

  deleteTicket: (id: string) => wormholeCall<{ ok: boolean }>('tickets.delete', { ticketId: id }),

  batchUpdateStatus: (data: { ticketIds: string[]; status: string; teamId: string }) =>
    wormholeCall<{ modified: number; lockedIds: string[] }>('tickets.batchStatus', data),

  /**
   * Assignment runs on Meteor: it fans out in-app + push notifications to
   * newly added assignees and emits the activity-log entry.
   */
  assignTicket: (id: string, assignedToUserIds: string[]) =>
    wormholeCall<Record<string, unknown>>('tickets.assign', {
      ticketId: id,
      assignedToUserIds,
    }).then(toTicket),

  /**
   * Link a ticket to a Redmine issue the caller can see, or move the link to a
   * different one. `expectedIssueId` is the link the caller last saw (null for
   * none); the server refuses with `stale-link` when it has changed since.
   */
  link: (id: string, issueId: number, expectedIssueId: string | null) =>
    wormholeCall<Record<string, unknown>>('tickets.link', {
      ticketId: id,
      issueId,
      expectedIssueId,
    }).then(toTicket),

  /**
   * The team's ticket carrying Redmine issue `issueId`, created from the issue
   * and linked when there is none (#736). `created` says which.
   */
  carryIssue: (teamId: string, issueId: string) =>
    wormholeCall<{ ticket: Record<string, unknown>; created: boolean }>('tickets.carryIssue', {
      teamId,
      issueId: Number(issueId),
    }).then(({ ticket, created }) => ({ ticket: toTicket(ticket), created })),

  /**
   * The tickets a search-bar query names, in any of the caller's teams
   * (#736c): an ID or ID prefix (`THX-12`, `TH-`, `RM0-233`, `pulse-2`, a bare
   * `223`), `@name`, a pasted issue link, or words in the title. Newest first
   * (an exact ID first of all), at most 25, without descriptions. `kind` is how
   * the query was read, null when it was not worth asking.
   */
  search: (query: string) =>
    wormholeCall<{ kind: TicketSearchKind | null; tickets: Array<Record<string, unknown>> }>(
      'tickets.search',
      { query },
    ).then(({ kind, tickets }) => ({ kind, tickets: tickets.map(toTicket) })),

  /** Remove a ticket's link. The ticket carries on as a plain TimeHuddle ticket. */
  unlink: (id: string, expectedIssueId: string) =>
    wormholeCall<Record<string, unknown>>('tickets.unlink', {
      ticketId: id,
      expectedIssueId,
    }).then(toTicket),

  /**
   * The outside issue the ticket carries, read with the caller's own key
   * (#757). Fails with `not-connected`, `other-server` / `custom-instance`, or
   * `gone` when they can't read it.
   */
  outsideIssue: (id: string) =>
    wormholeCall<OutsideIssue>('tickets.outsideRecord', { ticketId: id }),

  /**
   * Write edits to the carried issue as the caller. `expected` is the issue
   * they were looking at (`kind:server:key`, see `outsideIssueKey`) and its
   * `dates.updated` then: refused with `stale-link` when the ticket now
   * carries another issue, and `stale` when it changed. `mismatches` names
   * what the source stored differently.
   */
  writeOutside: (id: string, expected: { issue: string; updated: string }, edits: OutsideEdits) =>
    wormholeCall<Omit<OutsideIssue, 'kind' | 'me'> & { mismatches: string[] }>(
      'tickets.writeOutside',
      {
        ticketId: id,
        expectedIssue: expected.issue,
        expectedUpdatedAt: expected.updated,
        edits: { ...edits },
      },
    ),

  /** Who is timing the ticket right now. Cheap enough for a page to poll. */
  lockStatus: (id: string) =>
    wormholeCall<Pick<TicketLinkStatus, 'lock'>>('tickets.lockStatus', { ticketId: id }),

  /** The lock plus the caller's time figures, for a dialog about to change the link. */
  linkStatus: (id: string) =>
    wormholeCall<TicketLinkStatus>('tickets.linkStatus', { ticketId: id }),

  /** Get total accumulated seconds for a ticket from Timers. */
  getTotal: (ticketId: string) =>
    wormholeCall<{ totalSeconds: number }>('timers.getTicketTotal', { ticketId }).then(
      (r) => r.totalSeconds,
    ),
};

// ─── Huddle API ───────────────────────────────────────────────────────────────

/** An image/video/file attached to a huddle post. */
export interface HuddlePostAttachment {
  mediaId: string;
  type: 'image' | 'video' | 'file';
  url: string;
  thumbnailUrl?: string;
  filename?: string;
  /** Size in bytes, from the upload's media item (absent on older posts). */
  size?: number;
  /** An image's size in pixels, so its tile keeps its space before it loads. */
  width?: number;
  height?: number;
  /** A video's name, from its media item (null: unnamed). Videos only. */
  name?: string | null;
  /**
   * Who owns the video — the one who may rename it — which needn't be whoever
   * posted it (null: a video from before every video had a media item). Videos only.
   */
  ownerId?: string | null;
}

export interface HuddlePost {
  id: string;
  teamId: string;
  userId: string;
  userName: string;
  userInitials: string;
  content: {
    text: string;
    mentions: string[];
  };
  /**
   * Linked tickets; `title` is null when the ticket no longer exists. `label`
   * is how the feed names it (#788): `TH-7: Fix login`.
   */
  tickets?: { id: string; title: string | null; label?: string | null }[];
  attachments: HuddlePostAttachment[];
  likes: string[];
  commentCount: number;
  /** Client-local calendar date (YYYY-MM-DD) this post is the plan for. */
  postDate?: string;
  /** Clock session this post is the plan/wrap-up for (per-session gate). */
  clockEventId?: string;
  /** Resolved from `clockEventId` at read time; absent if the session is gone. */
  session?: { startTime: number; endTime: number | null };
  /** Set when the author saved a wrap-up edit (plan-first clock flow). */
  wrapUpAt?: string | null;
  /** Scheduled post (#596): only its author sees it, and nobody is notified, until then. */
  deliverAt?: string;
  /** When a scheduled post was delivered; absent while it is still waiting. */
  deliveredAt?: string;
  /** Bumped on every write to deliverAt/deliveredAt/postDate — lets a client
   *  merging a cached snapshot against a live one tell which is newer, since
   *  those writes deliberately don't touch updatedAt. */
  scheduleVersion?: number;
  createdAt: string;
  updatedAt: string;
}

/** One window of a feed; `hasMore` means older posts exist before `since`. */
export interface HuddleFeedPage {
  posts: HuddlePost[];
  hasMore: boolean;
}

export interface HuddleComment {
  id: string;
  postId: string;
  userId: string;
  userName: string;
  userInitials: string;
  userAvatarUrl?: string;
  content: string;
  mentions: string[];
  createdAt: string;
  updatedAt: string;
}

/** A post's text and who it addresses, as written to the server. */
export interface HuddlePostContentInput {
  text: string;
  /** User ids @mentioned. */
  mentions: string[];
  /** `@team` (everyone on the team) / `@here` (everyone clocked in to it), #784. */
  groupMentions?: Array<'team' | 'here'>;
}

export const huddleApi = {
  /** Fetch all huddle posts for a specific ticket. */
  getPostsByTicket: (ticketId: string) =>
    wormholeCall<{ posts: HuddlePost[] }>('huddle.getPostsByTicket', { ticketId }).then(
      (r) => r.posts,
    ),

  /**
   * Published huddle posts for a team over wormhole REST, from `since` (ISO
   * date; the last 30 days when omitted). Used to refresh the feed the moment
   * a post is created, since the DDP socket can be down (the WebView drops it
   * while backgrounded for a Pulse recording) and the live subscription would
   * otherwise deliver the new post only later.
   */
  getPosts: (teamId: string, since?: string) =>
    wormholeCall<HuddleFeedPage>('huddle.getPosts', since ? { teamId, since } : { teamId }),

  /** Whether the team has published posts before `since` — `hasMore` without
   *  the posts, for a feed already receiving them over the live subscription. */
  hasPostsBefore: (teamId: string, since: string) =>
    wormholeCall<HuddleFeedPage>('huddle.getPosts', { teamId, since, withPosts: false }).then(
      (r) => r.hasMore,
    ),

  /** The caller's own published posts across every team they belong to (the
   *  Huddle inbox's Personal view), from `since` (the last 30 days when omitted). */
  getMyPosts: (since?: string) =>
    wormholeCall<HuddleFeedPage>('huddle.getMyPosts', since ? { since } : {}),

  /** The caller's own post for a calendar date (YYYY-MM-DD) in a team, or null. */
  getMyPostForDate: (teamId: string, postDate: string) =>
    wormholeCall<{ post: HuddlePost | null }>('huddle.getMyPostForDate', {
      teamId,
      postDate,
    }).then((r) => r.post),

  /** The caller's post linked to a clock session (per-session gate), or null. */
  getMyPostForSession: (teamId: string, clockEventId: string) =>
    wormholeCall<{ post: HuddlePost | null }>('huddle.getMyPostForSession', {
      teamId,
      clockEventId,
    }).then((r) => r.post),

  /**
   * Create a huddle post.
   *
   * Post authoring goes over wormhole REST (CapacitorHttp / fetch) rather than
   * DDP: the WebView drops the DDP socket whenever the app is backgrounded —
   * recording a Pulse video, for instance — and a DDP-only write then strands
   * the post with no error until the socket reconnects. The feed itself still
   * updates in realtime via the DDP subscription when one is live.
   */
  createPost: (params: {
    teamId: string;
    content: HuddlePostContentInput;
    ticketIds?: string[];
    attachments?: HuddlePostAttachment[];
    postDate?: string;
    clockEventId?: string;
    wrapUp?: boolean;
    /** ISO date to schedule the post for. */
    deliverAt?: string;
  }) => wormholeCall<{ id: string }>('huddle.createPost', { ...params }),

  /** Deliver one of my scheduled posts now. */
  sendScheduledNow: (postId: string) =>
    wormholeCall<{ id: string }>('huddle.sendScheduledNow', { postId }),

  /** Move one of my scheduled posts to a new time; `postDate` is its new local date. */
  reschedulePost: (postId: string, schedule: { deliverAt: string; postDate?: string }) =>
    wormholeCall<{ id: string }>('huddle.reschedulePost', { postId, ...schedule }),

  /** Update a huddle post. Pass wrapUp to stamp wrapUpAt (plan-first clock flow).
   * Pass attachments/ticketIds to edit them (omit to leave untouched; `[]` clears tickets). */
  updatePost: (
    postId: string,
    content: HuddlePostContentInput,
    options?: {
      wrapUp?: boolean;
      attachments?: HuddlePostAttachment[];
      ticketIds?: string[];
    },
  ) => wormholeCall<{ id: string }>('huddle.updatePost', { postId, content, ...options }),

  /** Delete a huddle post. */
  deletePost: (postId: string) => getDdpClient().call('huddle.deletePost', { postId }),

  /** Toggle like on a post */
  toggleLike: (postId: string) => getDdpClient().call('huddle.toggleLike', { postId }),

  /** Get comments for a post */
  getComments: async (postId: string) => {
    const result = await getDdpClient().call('huddle.getComments', { postId });
    return Array.isArray(result) ? result : ((result as { comments?: unknown[] })?.comments ?? []);
  },

  /** Add a comment to a post */
  addComment: (postId: string, data: { content: string; mentions: string[] }) =>
    getDdpClient().call('huddle.addComment', { postId, ...data }),

  /** Delete a comment */
  deleteComment: (commentId: string) => getDdpClient().call('huddle.deleteComment', { commentId }),
};

// ─── Team API ─────────────────────────────────────────────────────────────────

/** Where a team setting's effective value comes from. */
export type SettingSource = 'team' | 'parentTeam' | 'org' | 'enterprise' | 'system' | 'personal';

/** A setting as the server resolved it: the client never applies its own default. */
export interface ResolvedSetting {
  value: boolean;
  source: SettingSource;
  locked: boolean;
  /** The team, org or enterprise the value comes from; absent for 'system' and 'personal'. */
  sourceId?: string;
}

export type TeamSettingKey = 'requirePlanForClock' | 'autoAcceptJoins';

/** Settings an org or enterprise can hold; `allowAutoJoin` has no team-level value. */
export type DefaultSettingKey =
  | TeamSettingKey
  | 'allowAutoJoin'
  // Notification defaults (#784): set and locked from the org or enterprise.
  | 'notifyAdminsOnClock'
  | 'shiftEndReminders';

/** `true`/`false` sets a value (or locks); `null` resets it to inherited (or unlocks). */
export type SettingsPatch = Partial<Record<DefaultSettingKey, boolean | null>>;

/** An org's or enterprise's own settings, its locks, and what each resolves to. */
export interface SettingsEditView {
  settings: Partial<Record<DefaultSettingKey, boolean>>;
  settingsLocks: Partial<Record<DefaultSettingKey, true>>;
  effectiveSettings: Record<DefaultSettingKey, ResolvedSetting>;
}

export interface Team {
  id: string;
  orgId: string;
  parentTeamId: string | null;
  name: string;
  description: string | null;
  members: string[];
  admins: string[];
  code: string;
  isPersonal: boolean;
  /** The prefix the team's tickets are shown with, from its organization: `TH` in `TH-12`. */
  ticketPrefix: string;
  /** The servers the team's organization has named, for outside IDs like `RM0-214`. */
  sourcePrefixes: SourcePrefix[];
  /** Plain booleans, kept for app versions that predate `resolvedSettings`. */
  settings: Record<TeamSettingKey, boolean>;
  resolvedSettings: Record<TeamSettingKey, ResolvedSetting>;
  createdAt: string;
  updatedAt: string | null;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  username: string | null;
  image: string | null;
}

export interface TeamJoinRequest {
  id: string;
  teamId: string;
  userId: string;
  teamCode: string;
  status: 'pending' | 'approved' | 'declined' | 'expired';
  requestedAt: string;
  respondedAt?: string;
  respondedBy?: string;
}

export interface TeamJoinRequestWithUser extends TeamJoinRequest {
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export interface TeamJoinRequestPreview {
  notificationId: string;
  requestId: string;
  teamId: string;
  teamName: string;
  teamDescription: string;
  requester: { id: string; name: string; email: string } | null;
  alreadyProcessed: boolean;
}

export interface TeamInvitation {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked' | 'delivery_failed';
  invitedByName: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
  revokedAt?: string;
}

export const teamApi = {
  getTeams: () =>
    wormholeCall<{ teams: Team[]; pendingRequests: TeamJoinRequest[] }>('teams.list', {}),

  getTeamsOnly: () => wormholeCall<{ teams: Team[] }>('teams.list', {}).then((r) => r.teams),

  ensurePersonal: () =>
    wormholeCall<{ team: Team }>('teams.ensurePersonal', {}).then((r) => r.team),

  createTeam: (data: {
    name: string;
    description?: string;
    orgId?: string;
    parentTeamId?: string | null;
  }) => wormholeCall<{ team: Team }>('teams.create', data).then((r) => r.team),

  getSubTeams: (id: string) =>
    wormholeCall<{ teams: Team[] }>('teams.subteams', { teamId: id }).then((r) => r.teams),

  joinTeam: (teamCode: string) =>
    wormholeCall<
      { status: 'pending'; request: TeamJoinRequest } | { status: 'joined'; team: Team }
    >('teams.join', { teamCode }),

  renameTeam: (id: string, newName: string) =>
    wormholeCall<{ team: Team }>('teams.rename', { teamId: id, newName }).then((r) => r.team),

  /** `true`/`false` sets a setting on the team; `null` resets it to the inherited value. */
  updateSettings: (id: string, settings: Partial<Record<TeamSettingKey, boolean | null>>) =>
    wormholeCall<{ team: Team }>('teams.updateSettings', { teamId: id, ...settings }).then(
      (r) => r.team,
    ),

  deleteTeam: (id: string) => wormholeCall<{ ok: boolean }>('teams.delete', { teamId: id }),

  getMembers: (id: string) =>
    wormholeCall<{ members: TeamMember[] }>('teams.getMembers', { teamId: id }).then((r) =>
      r.members.map((m) =>
        m.image && !/^https?:\/\//i.test(m.image)
          ? { ...m, image: `${TIMECORE_BASE_URL}${m.image.startsWith('/') ? '' : '/'}${m.image}` }
          : m,
      ),
    ),

  inviteMember: (id: string, email: string) =>
    wormholeCall<
      | { ok: true; status: 'joined' }
      | { ok: true; status: 'pending'; invitationId: string; expiresAt: string }
    >('teams.invite', { teamId: id, email }),

  removeMember: (id: string, userId: string) =>
    wormholeCall<{ ok: boolean }>('teams.removeMember', { teamId: id, userId }),

  setMemberRole: (id: string, userId: string, role: 'admin' | 'member') =>
    wormholeCall<{ ok: boolean }>('teams.setRole', { teamId: id, userId, role }),

  setMemberPassword: (id: string, userId: string, newPassword: string) =>
    wormholeCall<{ ok: boolean }>('teams.setMemberPassword', { teamId: id, userId, newPassword }),

  getPendingJoinRequests: (teamId: string) =>
    wormholeCall<{ requests: TeamJoinRequestWithUser[] }>('teams.getPendingJoinRequests', {
      teamId,
    }).then((r) => r.requests),

  approveJoinRequest: (requestId: string) =>
    wormholeCall<{ status: string }>('teams.approveJoinRequest', { requestId }),

  declineJoinRequest: (requestId: string) =>
    wormholeCall<{ status: string }>('teams.declineJoinRequest', { requestId }),

  getPendingInvitations: (teamId: string) =>
    wormholeCall<{ invitations: TeamInvitation[] }>('teams.getPendingInvitations', {
      teamId,
    }).then((r) => r.invitations),

  revokeInvitation: (invitationId: string) =>
    wormholeCall<{ ok: boolean }>('teams.revokeInvite', { invitationId }),
};

// ─── Clock API ────────────────────────────────────────────────────────────────

/**
 * One ticket-timer session that ran inside a shift, as the timesheet renders it.
 * `title`/`url` are resolved server-side at read time — a Huddle ticket links
 * in-app, a Redmine issue links out to the instance.
 */
export interface ShiftTicketSession {
  id: string;
  workItemId: string;
  source: TicketSourceId;
  ticketId: string;
  title: string | null;
  url: string | null;
  startTime: number;
  endTime: number | null;
  durationSeconds: number | null;
}

export interface ClockEvent {
  id: string;
  userId: string;
  teamId: string;
  startTime: number;
  /** @deprecated No longer returned by the API — use startTime. */
  originalStartTime?: number;
  accumulatedTime: number;
  breaks?: Array<{
    startTime: number;
    endTime: number | null;
    type?: 'rest' | 'meal';
    classificationSource?: 'auto' | 'manual';
    notes?: string;
  }>;
  workSeconds?: number;
  deductedBreakSeconds?: number;
  totalBreakSeconds?: number;
  isPaused?: boolean;
  /** @deprecated No longer set by the API — use breaks[].endTime === null to find active break. */
  pausedAt?: number | null;
  endTime: number | null;
  /** Ticket timers that ran during this shift. Only `clock.timesheet` returns these. */
  ticketSessions?: ShiftTicketSession[];
}

// ─── Timesheet change approvals ───────────────────────────────────────────────

/**
 * Justification attached to a retroactive timesheet change. Required on teams
 * that have someone to review it; the backend is the authority and rejects a
 * submission that arrives without one.
 */
export interface TimesheetJustification {
  description: string;
  videoUrl?: string;
}

export interface TimesheetChangeRequest {
  id: string;
  teamId: string;
  userId: string;
  kind: 'clock' | 'timer';
  action: 'create' | 'update' | 'delete';
  targetId: string | null;
  payload: Record<string, unknown>;
  /** Names what is being changed where an id alone would mean nothing (e.g. a ticket). */
  label: string | null;
  /** The entry's live state, resolved server-side at read time — null once it's gone. */
  current: {
    startTime?: number;
    endTime?: number | null;
    /** Paid seconds, so a break-only edit is visible to a reviewer. */
    accumulatedTime?: number | null;
    durationSeconds?: number;
    date?: string;
  } | null;
  description: string;
  videoUrl: string | null;
  /** The walkthrough's name (null: unnamed). */
  videoName?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  respondedAt: string | null;
  respondedBy: string | null;
  responseNote: string | null;
  requesterName?: string;
  teamName?: string | null;
  canReview?: boolean;
}

/** What a mutating timesheet call returns when it was queued for review instead. */
export interface PendingChange {
  pending: true;
  request: TimesheetChangeRequest;
}

export function isPendingChange(result: unknown): result is PendingChange {
  return typeof result === 'object' && result !== null && 'pending' in result;
}

export const timesheetApprovalApi = {
  /** Requests the caller raised — drives their own pending/declined badges. */
  listMine: (options?: { teamId?: string; status?: TimesheetChangeRequest['status'] }) =>
    wormholeCall<{ requests: TimesheetChangeRequest[] }>(
      'timesheetApprovals.listMine',
      options ?? {},
    ).then((r) => r.requests),

  /** Requests awaiting the caller's review across the teams they administer. */
  listPending: (teamId?: string) =>
    wormholeCall<{ requests: TimesheetChangeRequest[] }>(
      'timesheetApprovals.listPending',
      teamId ? { teamId } : {},
    ).then((r) => r.requests),

  get: (requestId: string) =>
    wormholeCall<TimesheetChangeRequest>('timesheetApprovals.get', { requestId }),

  approve: (requestId: string, note?: string) =>
    wormholeCall<TimesheetChangeRequest>('timesheetApprovals.approve', { requestId, note }),

  reject: (requestId: string, note: string) =>
    wormholeCall<TimesheetChangeRequest>('timesheetApprovals.reject', { requestId, note }),

  cancel: (requestId: string) =>
    wormholeCall<{ ok: boolean }>('timesheetApprovals.cancel', { requestId }).then((r) => r.ok),
};

export const clockApi = {
  /** Clock in to a team. Returns the new clock event. */
  start: (teamId: string, planPostId?: string) =>
    wormholeCall<ClockEvent>('clock.start', { teamId, planPostId }),

  /** Clock out of a team. */
  stop: (teamId: string) => wormholeCall<ClockEvent>('clock.stop', { teamId }),

  /** Pause an active clock session (break start). */
  pause: (teamId: string) => wormholeCall<ClockEvent>('clock.pause', { teamId }),

  /** Resume a paused clock session (break end). */
  resume: (teamId: string) => wormholeCall<ClockEvent>('clock.resume', { teamId }),

  /** Get active clock status for a team. */
  getStatus: (teamId: string) =>
    wormholeCall<{
      event: ClockEvent;
      workSeconds: number;
      isPaused: boolean;
    } | null>('clock.status', { teamId }),

  /** Get the current user's active clock event (any team), or null. */
  getActive: (_userId?: string) => wormholeCall<ClockEvent | null>('clock.activeForUser', {}),

  /** The caller's latest completed sessions on a team, newest first. */
  getRecentSessions: (teamId: string, limit: number) =>
    wormholeCall<ClockEvent[]>('clock.events', { teamId, completed: true, limit }),

  /** Get timesheet data for a user over a date range (epoch ms boundaries). */
  getTimesheet: (userId: string, startMs: number, endMs: number) =>
    wormholeCall<{
      sessions: ClockEvent[];
      summary: {
        totalSeconds: number;
        totalBreakSeconds: number;
        totalSessions: number;
        completedSessions: number;
        averageSessionSeconds: number;
        workingDays: number;
      };
    }>('clock.timesheet', { userId, startMs, endMs }),

  /** Update a clock event's timestamps and optional break intervals. */
  updateTimes: (
    clockEventId: string,
    data: {
      startTime?: number;
      endTime?: number | null;
      breaks?: Array<{ startTime: number; endTime: number | null }>;
    },
    justification?: TimesheetJustification,
  ) =>
    wormholeCall<ClockEvent | PendingChange>('clock.updateTimes', {
      clockEventId,
      ...data,
      ...justification,
    }),

  /** Delete a clock event. */
  deleteEvent: (clockEventId: string, justification?: TimesheetJustification) =>
    wormholeCall<{ ok: boolean } | PendingChange>('clock.deleteEvent', {
      clockEventId,
      ...justification,
    }),

  /** Create a completed manual clock entry for a past time range. */
  createManualEntry: (
    data: { teamId: string; startTime: number; endTime: number },
    justification?: TimesheetJustification,
  ) =>
    wormholeCall<ClockEvent | PendingChange>('clock.createManual', { ...data, ...justification }),
};

// ─── Team Dashboard API ───────────────────────────────────────────────────────

export interface TeamMemberClockStatus {
  userId: string;
  name: string;
  username: string | null;
  image: string | null;
  isClockedIn: boolean;
  isOnBreak: boolean;
  activeClockStart: number | null;
  todaySeconds: number;
}

export interface TeamRunningTimer {
  timerId: string;
  workItemId: string;
  userId: string;
  userName: string;
  userImage: string | null;
  ticketId: string;
  ticketTitle: string;
  ticketPriority: string | null;
  startTime: number;
}

export const teamDashboardApi = {
  getTeamClockStatus: (teamId: string) =>
    wormholeCall<{ members: TeamMemberClockStatus[] }>('clock.teamStatus', { teamId }).then((r) =>
      r.members.map((m) =>
        m.image && !/^https?:\/\//i.test(m.image)
          ? { ...m, image: `${TIMECORE_BASE_URL}${m.image.startsWith('/') ? '' : '/'}${m.image}` }
          : m,
      ),
    ),

  getTeamRunningTimers: (teamId: string) =>
    wormholeCall<{ timers: TeamRunningTimer[] }>('timers.getTeamRunning', { teamId }).then((r) =>
      r.timers.map((t) =>
        t.userImage && !/^https?:\/\//i.test(t.userImage)
          ? {
              ...t,
              userImage: `${TIMECORE_BASE_URL}${t.userImage.startsWith('/') ? '' : '/'}${t.userImage}`,
            }
          : t,
      ),
    ),
};

export interface Notification {
  id: string;
  userId: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  /** Which pop-ups the user's preferences allowed for it (#784); absent on older rows. */
  pushed?: { desktop: boolean; phone: boolean };
  read: boolean;
  /** When the user first read it (ISO); null if unread or read before #804. */
  readAt: string | null;
  createdAt: string; // ISO
}

export type TeamInvitePreview = {
  notificationId: string;
  teamId: string;
  teamName: string;
  teamDescription: string;
  inviter: { id: string; name: string; email: string } | null;
  members: { id: string; name: string; email: string }[];
  admins: { id: string; name: string; email: string }[];
  alreadyMember: boolean;
};

export const notificationApi = {
  /** Fetch the user's notification inbox. */
  getInbox: () =>
    wormholeCall<{ notifications: Notification[] }>('notifications.getInbox', {}).then(
      (r) => r.notifications,
    ),

  /** Mark a single notification as read; returns its stored read time (null if read before #804). */
  markOneRead: (id: string) =>
    wormholeCall<{ ok: boolean; readAt: string | null }>('notifications.markOneRead', {
      notificationId: id,
    }),

  /** Mark all notifications as read. */
  markAllRead: () => wormholeCall<{ ok: boolean; readAt: string }>('notifications.markAllRead', {}),

  /** Bulk-delete notifications by ID. */
  deleteMany: (ids: string[]) =>
    wormholeCall<{ deletedCount: number }>('notifications.deleteMany', { ids }),

  /** Fetch team-invite preview for a notification. */
  getInvitePreview: (id: string) =>
    wormholeCall<TeamInvitePreview>('notifications.getInvitePreview', { notificationId: id }),

  /** Accept or ignore a team invite. */
  respondToInvite: (id: string, action: 'join' | 'ignore') =>
    wormholeCall<{ ok: boolean }>('notifications.respondToInvite', { notificationId: id, action }),

  /** Fetch team join request preview for a notification. */
  getJoinRequestPreview: (id: string) =>
    wormholeCall<TeamJoinRequestPreview>('teams.getJoinRequestPreview', { notificationId: id }),

  /** Approve or decline a team join request. */
  respondToJoinRequest: (id: string, action: 'approve' | 'decline') =>
    wormholeCall<{ ok: boolean }>('teams.respondToJoinRequest', { notificationId: id, action }),

  /** Consent to auto-clockout at 8h — called when user clicks "Agree to Clock Out" on the shift reminder. */
  agreeClockout: (clockEventId: string) =>
    wormholeCall<{ ok: boolean }>('clock.agreeAutoClockout', { clockEventId }),

  /** Send a test push notification to the requesting user's devices. */
  testPush: () => wormholeCall<{ ok: boolean }>('notifications.testPush', {}),
};

/** A notification event's setting per device (#784): desktop is web push, phone is the app. */
export interface NotificationDevices {
  desktop: boolean;
  phone: boolean;
}

/** How much of one team's Huddle notifies the user. Absent means 'mentions'. */
export type HuddleTeamLevel = 'all' | 'mentions' | 'mute';

/** Quiet time: holds pop-ups, while everything is still logged under the bell. */
export interface NotificationQuiet {
  /** Hold Huddle pop-ups (not mentions) while clocked out. */
  holdOffShift: boolean;
  /** ISO time the snooze ends, or null. */
  snoozeUntil: string | null;
  /** Snoozed until the user next clocks in. */
  snoozeUntilClockIn: boolean;
  /** Skip Huddle pop-ups for a team whose Huddle the user has open. */
  skipWhileViewing: boolean;
}

/** The user's notification preferences: every configurable event's effective setting. */
export interface NotificationPrefs {
  events: Record<string, NotificationDevices>;
  /** Team id → level, for teams set off the default only. */
  teams: Record<string, HuddleTeamLevel>;
  quiet: NotificationQuiet;
  /** Events an organization has locked on: the user's own choice doesn't apply. */
  locked: string[];
}

/** A change to some events, team levels and/or quiet time. */
export interface NotificationPrefsPatch {
  events?: Record<string, Partial<NotificationDevices>>;
  teams?: Record<string, HuddleTeamLevel>;
  quiet?: Partial<NotificationQuiet>;
}

export const notificationPrefsApi = {
  /** Every event's effective setting — defaults for anything the user hasn't changed. */
  get: () => wormholeCall<NotificationPrefs>('notificationPrefs.get', {}),

  /** Apply a change; resolves to the full, updated preferences. */
  update: (patch: NotificationPrefsPatch) =>
    wormholeCall<NotificationPrefs>('notificationPrefs.update', { ...patch }),
};

/** Identity-only reference to a ticket, independent of source (Huddle, Redmine, …). */
export interface MyBoardRef {
  sourceId: string;
  ticketId: string;
}

/** A "My Board" entry as stored server-side — no title/status snapshot. */
export interface MyBoardEntry extends MyBoardRef {
  addedAt: string;
  /** A Huddle ticket the caller can no longer see (deleted, or a team they left). */
  unavailable?: boolean;
}

export const myBoardApi = {
  list: () => wormholeCall<{ entries: MyBoardEntry[] }>('myBoard.list', {}).then((r) => r.entries),

  /** Add tickets to the signed-in user's My Board. Idempotent — re-adding is a no-op. */
  addMany: (refs: MyBoardRef[]) =>
    wormholeCall<{ addedCount: number }>('myBoard.addMany', { refs }),

  removeMany: (refs: MyBoardRef[]) =>
    wormholeCall<{ removedCount: number }>('myBoard.removeMany', { refs }),
};

// ─── Attachments ──────────────────────────────────────────────────────────────
export type AttachmentKind = 'clock' | 'ticket';
/** The kind a ticket page attaches to. */
export type TicketAttachmentKind = Exclude<AttachmentKind, 'clock'>;
export type AttachmentType = 'video' | 'image' | 'link';

export interface Attachment {
  id: string;
  url: string;
  type: AttachmentType;
  title: string | null;
  attachedTo: { kind: AttachmentKind; id: string };
  addedBy: string;
  addedAt: string;
  /** A video's name, from its media item (null: unnamed). Videos only. */
  name?: string | null;
  /** Whether the caller owns the video and so may rename it. Videos only. */
  canRename?: boolean;
}

export const attachmentApi = {
  list: (kind: AttachmentKind, id: string) =>
    wormholeCall<{ attachments: Attachment[] }>('attachments.list', { kind, id }).then(
      (r) => r.attachments,
    ),

  remove: (id: string) => wormholeCall<{ ok: boolean }>('attachments.remove', { attachmentId: id }),
};

// ─── Timer API ────────────────────────────────────────────────────────────────

/** Where a ticket comes from. Huddle's own tickets, or a linked Redmine instance. */
export type TicketSourceId = 'huddle' | 'redmine';

/**
 * A WorkItem is the per-user per-source per-ticket per-day timesheet row.
 * `displayTitle`/`displayUrl` are resolved server-side on read, never stored.
 */
export interface WorkItem {
  id: string;
  userId: string;
  source: TicketSourceId;
  ticketId: string;
  displayTitle: string | null;
  displayUrl: string | null;
  date: string; // UTC "YYYY-MM-DD"
  note?: string;
  createdAt: string;
  updatedAt?: string;
}

/** A Timer is one start–stop interval inside a WorkItem. */
export interface Timer {
  id: string;
  workItemId: string;
  userId: string;
  /** The shift this session ran inside. Null for sessions that predate that link. */
  clockEventId: string | null;
  date: string;
  startTime: number; // epoch ms
  endTime: number | null;
  durationSeconds?: number;
  createdAt: string;
}

export interface DayEntry {
  entry: WorkItem;
  sessions: Timer[];
}

export interface WeekDay {
  date: string;
  totalSeconds: number;
}

/** Returns the browser's IANA timezone string (e.g. "America/New_York"). */
function clientTz(): string {
  if (FORCED_TIMEZONE) {
    try {
      // Validate the IANA timezone so bad local values do not break API calls.
      new Intl.DateTimeFormat('en-US', { timeZone: FORCED_TIMEZONE }).format(new Date());
      return FORCED_TIMEZONE;
    } catch {
      // Fall back to browser timezone.
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** One of the caller's timer sessions on a ticket. `endTime` is null while running. */
export interface TicketSession {
  id: string;
  date: string;
  startTime: number;
  endTime: number | null;
  durationSeconds: number | null;
}

/**
 * The Huddle update a timer start or stop posted on the user's behalf, for
 * linking to it. Null when nothing was posted.
 */
export interface TimerUpdateRef {
  postId: string;
  teamId: string;
}

/**
 * What a timer session that just closed left to send to Redmine: its issue-day
 * and the hours not yet sent. Null when there is nothing worth sending (not a
 * Redmine issue, no linked account, or time an earlier entry already covers).
 */
export interface UnsentRedmineTime {
  ticketId: string;
  date: string;
  hours: number;
}

export const timerApi = {
  /**
   * Create a WorkItem for the given ticket + date. Optionally start a timer.
   * `source` defaults to `'huddle'` server-side. Starting a timer requires an
   * active shift and rejects with `no-active-shift` when there is none.
   *
   * A start posts an update to Huddle. `discardSessionId` names the running
   * session whose own update to remove, for a stint the user chose not to
   * keep; it is ignored unless that is the session the start takes over from.
   */
  createEntry: (data: {
    ticketId: string;
    source?: TicketSourceId;
    date: string;
    note?: string;
    notifyAdmins?: boolean;
    startNow?: boolean;
    discardSessionId?: string;
  }) =>
    wormholeCall<{
      entry: WorkItem;
      session: Timer | null;
      update?: TimerUpdateRef | null;
      /** From the session this start closed, if any. */
      unsentRedmine?: UnsentRedmineTime | null;
    }>('timers.createEntry', { ...data, tz: clientTz() }),

  /** Start a timer for a WorkItem. Closes any open timer first. `discardSessionId` as in `createEntry`. */
  startSession: (entryId: string, now?: number, discardSessionId?: string) =>
    wormholeCall<{
      session: Timer;
      closedSessionId?: string | null;
      update?: TimerUpdateRef | null;
      unsentRedmine?: UnsentRedmineTime | null;
    }>('timers.startSession', {
      entryId,
      now: now ?? Date.now(),
      tz: clientTz(),
      discardSessionId,
    }),

  /**
   * Stop a running timer, which posts an update to Huddle. `discardUpdate`
   * removes the session's own "Started" update instead and posts nothing.
   */
  stopSession: (sessionId: string, now?: number, discardUpdate = false) =>
    wormholeCall<{
      session: Timer;
      update?: TimerUpdateRef | null;
      unsentRedmine?: UnsentRedmineTime | null;
    }>('timers.stopSession', {
      sessionId,
      now: now ?? Date.now(),
      discardUpdate,
    }),

  /** Update a WorkItem's note, duration, and/or ticket (duration ignored while running). */
  updateEntry: (
    entryId: string,
    data: { note?: string | null; durationSeconds?: number; ticketId?: string },
    justification?: TimesheetJustification,
  ) =>
    wormholeCall<{ entry: WorkItem } | PendingChange>('timers.updateEntry', {
      entryId,
      ...data,
      ...justification,
    }),

  /** Delete a WorkItem and all of its timers. */
  deleteEntry: (
    entryId: string,
    options?: { notifyAdmins?: boolean },
    justification?: TimesheetJustification,
  ) =>
    wormholeCall<{ deletedEntry: boolean; deletedSessions: number } | PendingChange>(
      'timers.deleteEntry',
      {
        entryId,
        notifyAdmins: options?.notifyAdmins ?? true,
        ...justification,
      },
    ),

  /** Get the currently running timer for the authenticated user, or null. */
  getRunning: () =>
    wormholeCall<{ session: Timer | null }>('timers.getRunning', {}).then((r) => r.session),

  /** Get all entries + sessions for today in local time. Admin can pass userId. */
  getToday: (userId?: string) => {
    const tz = clientTz();
    return wormholeCall<{ entries: DayEntry[] }>('timers.getToday', {
      tz,
      ...(userId ? { userId } : {}),
    }).then((r) => r.entries);
  },

  /** Get all entries + sessions for a local day (YYYY-MM-DD). */
  getDay: (date: string) => {
    const tz = clientTz();
    return wormholeCall<{ entries: DayEntry[] }>('timers.getDay', { date, tz }).then(
      (r) => r.entries,
    );
  },

  /** Get 7-day totals for the week starting at the given date (YYYY-MM-DD). */
  getWeek: (date: string) => {
    const tz = clientTz();
    return wormholeCall<{ days: WeekDay[] }>('timers.getWeek', { date, tz }).then((r) => r.days);
  },

  /** The caller's own timer sessions on one ticket, newest first (running one included). */
  getTicketSessions: (ticketId: string, source: TicketSourceId) =>
    wormholeCall<{ sessions: TicketSession[] }>('timers.getTicketSessions', {
      ticketId,
      source,
    }).then((r) => r.sessions),

  /** Get total seconds for a ticket from all closed Timers. */
  getTicketTotal: (ticketId: string, source: TicketSourceId = 'huddle') =>
    wormholeCall<{ totalSeconds: number }>('timers.getTicketTotal', { ticketId, source }).then(
      (r) => r.totalSeconds,
    ),

  /**
   * Copy entries from the most recent previous day into toDate.
   * Skips rows that already exist with the same ticket + note + sortOrder signature.
   */
  copyPrevious: (toDate: string) =>
    wormholeCall<{ created: number }>('timers.copyPrevious', { toDate }).then((r) => r.created),
};

// ─── PulseVault video uploads ──────────────────────────────────────────────────────────────────────────────

/**
 * Where a Pulse video lands: the uploader's media library, the attachments of
 * a ticket, a Redmine issue or a clock session, a new post in a team's
 * Huddle, or the walkthrough on a pending timesheet change. The server delivers it there the moment it lands (see
 * meteor-backend/server/pulse-destinations.js). `post` is a video file added
 * to a Huddle post, which the composer attaches itself; it stays out of the library.
 */
export type PulseDestination =
  | { kind: 'library' }
  | { kind: 'post' }
  | { kind: 'walkthrough' }
  | { kind: AttachmentKind; id: string }
  | { kind: 'huddle'; teamId: string }
  | { kind: 'clock-plan'; teamId: string; postDate: string }
  | { kind: 'clock-wrapup'; clockEventId: string; postDate: string }
  | { kind: 'timesheet-request'; id: string };

/**
 * Where a Pulse upload stands. `done` carries the backend's `note` (what it
 * did, written for the status route — the UI says it in its own words);
 * `kept` means it couldn't go where it was meant to (its destination is gone)
 * and `reason` says why; `expired` means the link's token no longer works.
 */
export interface PulseUploadStatus {
  state: 'waiting' | 'done' | 'kept' | 'expired';
  note?: string;
  reason?: string;
}

async function readPulseStatus(
  videoid: string,
  uploadToken: string,
  signal?: AbortSignal,
): Promise<PulseUploadStatus> {
  const res = await fetch(`${METEOR_API_BASE}/pulsevault/artifacts/${videoid}/status`, {
    headers: { Authorization: `Bearer ${uploadToken}` },
    cache: 'no-store',
    signal,
  });
  if (res.status === 401 || res.status === 403) return { state: 'expired' };
  if (!res.ok) throw new Error(`Pulse status ${res.status}`);
  // `acknowledged` only says the backend finished handling it; the outcome
  // says whether that was a delivery or a refusal.
  const { outcome } = (await res.json()) as {
    outcome?: { state?: string; note?: string; reason?: string };
  };
  if (outcome?.state === 'done') return { state: 'done', note: outcome.note };
  if (outcome?.state === 'kept') return { state: 'kept', reason: outcome.reason };
  return { state: 'waiting' };
}

/** What a video's watch page shows (`videos.publicInfo`). */
export interface VideoPublicInfo {
  videoId: string;
  /** Its name, or null when it has none. */
  name: string | null;
  addedAt: string | null;
  /** Who added it — only for someone signed in. `image` is absolute. */
  author: { name: string | null; image: string | null } | null;
  /** The caller added it, so they may rename it. */
  canRename: boolean;
}

export const videoApi = {
  /** A video's watch-page details; works signed out (see VideoPublicInfo). */
  publicInfo: (videoId: string) =>
    wormholeCall<VideoPublicInfo>('videos.publicInfo', { videoId }).then((info) => ({
      ...info,
      author: info.author && {
        ...info.author,
        image: toAbsoluteUrl(info.author.image),
      },
    })),

  /**
   * Name one of the caller's own videos by its id (empty clears it). One name
   * per video, so it shows everywhere the video appears. Resolves the name kept.
   */
  rename: (videoId: string, name: string) =>
    wormholeCall<{ name: string | null }>('videos.rename', { videoId, name }).then((r) => r.name),

  /** Shared authenticated TUS upload endpoint for ticket and media-library uploads. */
  uploadEndpoint: () => `${METEOR_API_BASE}/pulsevault/upload`,

  /**
   * Shared TUS retry backoff for every PulseVault upload path. No leading `0`
   * on purpose: an immediate retry can race the still-streaming PATCH (the
   * proxy buffers and doesn't abort it), so the backend sees two concurrent
   * PATCHes at the same offset and 409s the second one.
   */
  uploadRetryDelays: [3000, 5000, 10000] as number[],

  /**
   * PulseVault's refusals of the file itself ("That file isn't a video.",
   * "That file is larger than 500 MB."), whose body is written to be shown.
   */
  uploadRefusalStatuses: [400, 413, 422] as number[],

  /**
   * Don't retry a refusal: the same bytes are refused again. Nor a `409
   * Upload-Offset conflict`: it means a concurrent/duplicate PATCH already
   * advanced the offset, so retrying only conflicts again. Every upload path
   * shares this so ticket and huddle behave identically.
   */
  shouldRetryUpload: (err: DetailedError): boolean => {
    const status = err.originalResponse?.getStatus() ?? 0;
    return status !== 409 && !videoApi.uploadRefusalStatuses.includes(status);
  },

  /**
   * Where a Pulse upload stands, from PulseVault's status route read with the
   * upload's own token. `waiting` until the backend has delivered the video (it
   * is made web-playable first, so this can come seconds after the last byte);
   * then `done` with the backend's note, or `kept` with the reason when it
   * decided not to deliver it (its destination is gone); `expired` once the
   * token no longer opens the status. Throws on a network failure.
   */
  status: readPulseStatus,

  /**
   * Wait for the backend to file a finished upload and say how it went.
   * `forbidden` means the token no longer opens the status; `timeout` means
   * nothing was recorded within `timeoutMs` — the backend keeps trying on its
   * own, so the video may still appear later.
   */
  waitUntilFiled: async (
    videoid: string,
    uploadToken: string,
    timeoutMs = 120_000,
  ): Promise<
    | { state: 'done'; note?: string }
    | { state: 'kept'; reason?: string }
    | { state: 'forbidden' }
    | { state: 'timeout' }
  > => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      try {
        // Each request is bounded by what's left of the deadline (at most 10 s), so a stalled
        // request can't keep this waiting past `timeoutMs`; an abort counts as transient.
        const status = await readPulseStatus(
          videoid,
          uploadToken,
          AbortSignal.timeout(Math.max(1000, Math.min(10_000, deadline - Date.now()))),
        );
        if (status.state === 'expired') return { state: 'forbidden' };
        if (status.state === 'done') return { state: 'done', note: status.note };
        if (status.state === 'kept') return { state: 'kept', reason: status.reason };
      } catch {
        // A transient failure: ask again.
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    return { state: 'timeout' };
  },

  /**
   * Reserve a Pulse upload: a videoid and a link token that carries where the
   * finished video goes. The backend delivers it there once it lands.
   */
  reserve: (destination: PulseDestination) =>
    wormholeCall<{ videoid: string; uploadToken: string }>('pulsevault.reserve', { destination }),
};

// ─── Media Library ────────────────────────────────────────────────────────────

export interface MediaItem {
  id: string;
  userId: string;
  type: 'video' | 'image' | 'document';
  mimeType: string;
  url: string;
  videoid: string | null;
  filename: string;
  size: number;
  title: string | null;
  caption: string | null;
  altText: string | null;
  thumbnail: string | null;
  uploadedAt: string;
}

function withAbsoluteMediaItem(item: MediaItem): MediaItem {
  const thumbnail = toAbsoluteUrl(item.thumbnail);
  const url = toAbsoluteUrl(item.url) ?? item.url;
  if (thumbnail === item.thumbnail && url === item.url) return item;
  return { ...item, thumbnail, url };
}

export const mediaApi = {
  /**
   * Upload an image or document to the media library. Images are downscaled
   * and re-encoded first — a full-resolution camera photo is mostly pixels
   * nothing in the app ever renders.
   *
   * Uses XMLHttpRequest rather than `fetch` because only XHR exposes
   * upload-side byte progress, which the composer needs to render one progress
   * bar across image, document, and (TUS) video uploads alike.
   */
  uploadImage: async (file: File, onProgress?: (fraction: number) => void): Promise<MediaItem> => {
    const { compressImageForUpload } = await import('./imageCompress');
    const upload = await compressImageForUpload(file);

    const form = new FormData();
    form.append('file', upload, upload.name || 'image');
    const token = await getAccessToken();

    const { status, body } = await new Promise<{ status: number; body: string }>(
      (resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${METEOR_API_BASE}/api/media/upload`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
        };
        xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
        xhr.onerror = () => reject(new ApiError('Upload failed', 0));
        xhr.onabort = () => reject(new ApiError('Upload cancelled', 0));
        xhr.send(form);
      },
    );

    const parsed = (() => {
      try {
        return JSON.parse(body) as { item?: MediaItem; error?: string };
      } catch {
        return {} as { item?: MediaItem; error?: string };
      }
    })();

    if (status < 200 || status >= 300 || !parsed.item) {
      throw new ApiError(parsed.error ?? `HTTP ${status}`, status);
    }
    return withAbsoluteMediaItem(parsed.item);
  },

  list: () =>
    wormholeCall<{ items: MediaItem[] }>('media.list', {}).then((r) =>
      r.items.map(withAbsoluteMediaItem),
    ),

  listForUser: (userId: string) =>
    wormholeCall<{ items: MediaItem[] }>('media.listForUser', { userId }).then((r) =>
      r.items.map(withAbsoluteMediaItem),
    ),

  uploadThumbnail: async (id: string, blob: Blob): Promise<MediaItem> => {
    const form = new FormData();
    form.append('file', blob, 'thumbnail.jpg');
    const token = await getAccessToken();
    const res = await fetch(`${METEOR_API_BASE}/api/media-thumbnail/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError((body.error as string) ?? `HTTP ${res.status}`, res.status);
    }
    const data = (await res.json()) as { item: MediaItem };
    return withAbsoluteMediaItem(data.item);
  },
};

// ─── Activity Log ─────────────────────────────────────────────────────────────

export interface ActivityLogItem {
  id: string;
  userId: string;
  teamId?: string;
  type: string;
  actor: { id: string; name: string; avatar?: string };
  payload: Record<string, unknown>;
  occurredAt: string; // ISO 8601
  source: string;
}

export const activityApi = {
  getLog: (params: { limit?: number; before?: string } = {}) =>
    wormholeCall<{ events: ActivityLogItem[]; nextCursor: string | null }>('activity.log', params),

  getUserActivity: (userId: string, params: { limit?: number; before?: string } = {}) =>
    wormholeCall<{ events: ActivityLogItem[]; nextCursor: string | null }>('activity.userLog', {
      userId,
      ...params,
    }),

  /** Ticket IDs + titles from the user's last 48 h of timer work. */
  getUserWorkSummary: (userId: string) =>
    wormholeCall<{ items: { id: string; title: string }[] }>('timers.getUserWorkSummary', {
      userId,
    }),

  getTicketActivity: (ticketId: string, limit = 50) =>
    wormholeCall<{ events: ActivityLogItem[] }>('activity.ticketActivity', { ticketId, limit }),
};

// ─── Channel types ────────────────────────────────────────────────────────────

export interface PersonalAccessToken {
  _id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string | null;
}

export const tokenApi = {
  list: (): Promise<PersonalAccessToken[]> =>
    wormholeCall<{ tokens: PersonalAccessToken[] }>('tokens.list', {}).then((r) => r.tokens),

  create: (name: string): Promise<{ token: string; name: string }> =>
    wormholeCall<{ token: string; name: string }>('tokens.create', { name }),

  revoke: (id: string): Promise<void> =>
    wormholeCall<{ success: boolean }>('tokens.revoke', { tokenId: id }).then(() => undefined),
};

// ─── Redmine account link ───────────────────────────────────────────────────

/** Client-safe Redmine connection status. Never carries the API key. */
export interface RedmineStatus {
  connected: boolean;
  redmineUserId?: number;
  redmineLogin?: string;
  redmineName?: string;
  baseUrl?: string;
  linkedAt?: string | null;
  /** Whether this deployment lets users link their own Redmine URL (dev/test only). */
  customUrlAllowed?: boolean;
  /** The server's Redmine URL — what a custom URL field starts from. */
  defaultBaseUrl?: string | null;
}

/**
 * Why an issue is in the relevant list, strongest first. A row shows only the
 * first one — the server sorts them, so the choice is not the client's to make.
 */
export type RedmineRelevanceReason =
  'running' | 'assigned' | 'logged' | 'activity' | 'watching' | 'pinned' | 'board';

/** How the server read a search query, so an empty result can be explained. */
export type RedmineSearchKind = 'id' | 'url' | 'assignee' | 'text';

/** What TimeHuddle may remember about a Redmine issue. `null` lifts a hide; a pin stays. */
export type RedmineIssuePrefState = 'dismissed' | null;

/** Why a previewed ticket-day cannot be sent, or null when it can. */
export type RedmineBlockedReason = 'too-short' | 'issue-unavailable' | 'no-activity';

/** One ticket-day as the confirmation dialog renders it. */
export interface RedmineTimeEntryRow {
  ticketId: string;
  /** The day being logged, `YYYY-MM-DD`. */
  date: string;
  /** Seconds not yet sent to Redmine — what this push would cover. */
  seconds: number;
  /** Seconds already sent for this ticket-day by earlier pushes. */
  alreadySentSeconds: number;
  /** The seconds of each timer run that `seconds` adds up, oldest first. */
  runSeconds: number[];
  /** Decimal hours for `seconds`, rounded once to 2dp — what Redmine will store. */
  hours: number;
  subject: string | null;
  trackerName: string | null;
  issueMissing: boolean;
  activityId: number | null;
  activityName: string | null;
  /** Which rule chose the activity: `tracker`, `is_default`, `named`, … */
  activityReason: string;
  /** The activities this issue's project allows: the only ones the row may be sent under. */
  activityOptions: { id: number; name: string }[];
  /**
   * The Billable custom field the entry is sent with, copied from the newest
   * time entry that has one; null when Redmine has none to copy (#686).
   */
  billable: RedmineBillable | null;
  /** The minutes `hours` was rounded up to, and the exact hours; null when sent exact. */
  rounding?: { step: number; exactHours: number } | null;
  blockedReason: RedmineBlockedReason | null;
}

/** A Redmine time entry's Billable custom field: `1`/`0` for yes/no, or a list value. */
export interface RedmineBillable {
  id: number;
  name: string;
  value: string | string[];
}

export interface RedmineTimeEntryPreview {
  connected: boolean;
  /** False while a shift is open or a ticket timer is running. */
  idle: boolean;
  rows: RedmineTimeEntryRow[];
  baseUrl: string | null;
}

export interface RedmineTimeEntryPushRequest {
  ticketId: string;
  date: string;
  /** Optional override of the resolved activity. */
  activityId?: number;
  /** The entry's comment. Without one the entry says it was logged by TimeHuddle. */
  comment?: string;
  /**
   * The Billable value the user was shown, `null` for none. The server sends its
   * own lookup, and refuses the entry if that no longer matches what was shown.
   */
  billable?: RedmineBillable | null;
  /**
   * The hours the user was shown. The server works them out again, and refuses
   * the entry if its rounding has since changed them.
   */
  hours?: number;
}

/** Per-entry outcome — a partial failure leaves the successful rows synced. */
export interface RedmineTimeEntryPushOutcome {
  ticketId: string | null;
  date: string | null;
  hours?: number;
  ok: boolean;
  /** Present on failure: `no-log-time-permission`, `unreachable`, … */
  reason?: string;
  /** Redmine's own messages for a `rejected-by-redmine`, e.g. "Activity is not included in the list". */
  detail?: string[];
  entryId?: number;
  storedHours?: number;
}

export interface RedmineTimeEntryPushResult {
  results: RedmineTimeEntryPushOutcome[];
}

/** One ticket-day's outcome, with the activity it was sent under. */
export interface RedmineTicketDaySendOutcome extends RedmineTimeEntryPushOutcome {
  activityName?: string | null;
}

/** A Redmine `{ id, name }` reference (project, assignee, priority, tracker). */
export interface RedmineNamed {
  id: number;
  name: string;
}

/**
 * A Redmine issue status. `isClosed` comes from Redmine's own `is_closed` flag —
 * statuses are instance-defined free text, so the name alone cannot tell us
 * whether an issue is closed.
 */
export interface RedmineIssueStatus extends RedmineNamed {
  isClosed: boolean;
}

/** Read-only Redmine issue shape, as shaped server-side by `redmine-issues.js`. */
export interface RedmineIssue {
  id: number;
  subject: string;
  project: RedmineNamed | null;
  status: RedmineIssueStatus | null;
  assignedTo: RedmineNamed | null;
  priority: RedmineNamed | null;
  tracker: RedmineNamed | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Response for the Redmine issue lists. `connected: false` → user has no link. */
export interface RedmineIssueList {
  connected: boolean;
  baseUrl: string | null;
  issues: RedmineIssue[];
}

/**
 * A Redmine issue in the relevant list, with the server's reasons and score.
 * The list arrives sorted, so a client must not re-rank it.
 */
export interface RedmineRelevantIssue extends RedmineIssue {
  reasons: RedmineRelevanceReason[];
  score: number;
  /** When the user last logged time against it — only on a `logged` match. */
  lastTimeLoggedAt?: string;
}

/**
 * Response for `redmine.issues.relevant`. `partial: true` means one of the
 * server's signals timed out or failed, so the list is short rather than wrong:
 * show it, and say so quietly.
 */
export interface RedmineRelevantIssueList extends RedmineIssueList {
  issues: RedmineRelevantIssue[];
  partial: boolean;
  /**
   * My Board issue ids Redmine was asked for and did not return — deleted, or no
   * longer visible to the caller. Never an issue that merely failed to load.
   * Absent when the account is not connected.
   */
  unavailableBoardIds?: number[];
  /**
   * The issues that tickets the caller can see are linked to, read with the
   * caller's own key. They are not rows of their own: the Tickets page shows
   * them on the ticket that links to them. Absent when not connected.
   */
  linkedIssues?: RedmineIssue[];
}

/** Response for `redmine.issues.search`. At most 25 issues, titles matched only. */
export interface RedmineSearchResult extends RedmineIssueList {
  kind: RedmineSearchKind;
}

/**
 * One Redmine issue as the edit form sees it: the list shape plus its
 * description, author, and the statuses the caller may move it to (the current
 * status first — Redmine's workflow for this user decides the rest).
 */
export interface RedmineIssueDetail extends RedmineIssue {
  description: string;
  author: RedmineNamed | null;
  allowedStatuses: RedmineIssueStatus[];
}

/** One field change in a Redmine journal; `from`/`to` are null when not shown. */
export interface RedmineJournalChange {
  field: string;
  from: string | null;
  to: string | null;
}

/** One entry of an issue's Redmine history: a comment, field changes, or both. */
export interface RedmineJournal {
  id: number;
  user: RedmineNamed | null;
  createdAt: string | null;
  notes: string;
  changes: RedmineJournalChange[];
}

/**
 * Time logged on an issue in Redmine, by anyone. `spentOn` is the day the work
 * was for (`YYYY-MM-DD`); `createdAt` is when it was logged.
 */
export interface RedmineTimeEntry {
  id: number;
  user: RedmineNamed | null;
  hours: number;
  activity: RedmineNamed | null;
  comments: string;
  spentOn: string | null;
  createdAt: string | null;
}

/** A Redmine issue priority. `isDefault` is the instance's own default. */
export interface RedminePriority extends RedmineNamed {
  isDefault: boolean;
}

/** What the create/edit form offers for one project. `me` is the caller's Redmine user id. */
export interface RedmineFormOptions {
  trackers: RedmineNamed[];
  assignees: RedmineNamed[];
  priorities: RedminePriority[];
  defaultPriorityId: number | null;
  me: number | null;
}

/** Fields for creating an issue. Omitted/null optional fields take Redmine's defaults. */
export interface RedmineIssueCreateInput {
  projectId: number;
  subject: string;
  trackerId?: number | null;
  description?: string;
  assigneeId?: number | null;
  priorityId?: number | null;
}

/** Fields an edit may change. Absent keys are left alone; `assigneeId: null` unassigns. */
export interface RedmineIssueEdits {
  statusId?: number;
  priorityId?: number;
  assigneeId?: number | null;
  description?: string;
}

/**
 * Result of a create or update. `mismatches` lists Redmine field names it
 * stored differently from what was sent (confirmed by read-back).
 */
export interface RedmineIssueWriteResult {
  baseUrl: string | null;
  issue: RedmineIssueDetail | null;
  mismatches: string[];
}

/** Create additionally reports the new id, and `confirmed: false` if the read-back failed. */
export interface RedmineIssueCreateResult extends RedmineIssueWriteResult {
  issueId: number;
  confirmed: boolean;
}

export const githubApi = {
  status: (): Promise<GitHubLinkStatus> => wormholeCall<GitHubLinkStatus>('github.status', {}),

  /**
   * Where to send the browser to link a GitHub account to the caller. The
   * backend runs the OAuth round trip and returns to `/app/settings`, with
   * `?github_confirm=` for `confirmLink` or `?github=` saying why it refused.
   */
  linkUrl: async (): Promise<string> => {
    const { token } = await wormholeCall<{ token: string }>('github.linkToken', {});
    return `${METEOR_BASE_URL}/auth/github?${new URLSearchParams({ link: token })}`;
  },

  /** Finish the link the OAuth callback staged, as the user who started it. */
  confirmLink: (code: string): Promise<{ result: string; status: GitHubLinkStatus }> =>
    wormholeCall<{ result: string; status: GitHubLinkStatus }>('github.confirmLink', { code }),

  disconnect: (): Promise<GitHubLinkStatus> =>
    wormholeCall<GitHubLinkStatus>('github.disconnect', {}),
};

export const redmineApi = {
  status: (): Promise<RedmineStatus> => wormholeCall<RedmineStatus>('redmine.status', {}),

  /**
   * Validate and link a personal Redmine API key. `baseUrl` picks the instance,
   * and is honoured only when the status reports `customUrlAllowed`.
   */
  connect: (apiKey: string, baseUrl?: string): Promise<RedmineStatus> =>
    wormholeCall<RedmineStatus>('redmine.connect', { apiKey, ...(baseUrl ? { baseUrl } : {}) }),

  disconnect: (): Promise<RedmineStatus> => wormholeCall<RedmineStatus>('redmine.disconnect', {}),

  issues: {
    /**
     * The issues most likely to be what the caller is looking for: assigned to
     * them, recently logged against, recently touched, watched, pinned, or being
     * timed right now. Merged and sorted server-side, at most 100.
     *
     * `includeDismissed` keeps the issues the user hid from their suggestions —
     * the Tickets table passes it, because a dismissal is about the search
     * dropdown and must not reshape the table.
     */
    relevant: (includeDismissed = false): Promise<RedmineRelevantIssueList> =>
      wormholeCall<RedmineRelevantIssueList>('redmine.issues.relevant', { includeDismissed }),

    /**
     * Find issues by number (`1234`, `#1234`), a pasted Redmine link, `@person`,
     * or words matched against issue **titles** only. At most 25 results.
     *
     * The query never leaves this call: do not put it in the URL, in storage or
     * in analytics. A user may type a patient's name here.
     */
    search: (query: string): Promise<RedmineSearchResult> =>
      wormholeCall<RedmineSearchResult>('redmine.issues.search', { query }),

    /**
     * One page (100) of the open issues assigned to the caller in Redmine, most
     * recently updated first, whatever they removed from their Tickets table
     * (#736). `nextOffset` starts the next page; null at the end.
     */
    assignedIds: (offset = 0): Promise<{ issueIds: number[]; nextOffset: number | null }> =>
      wormholeCall<{ issueIds: number[]; nextOffset: number | null }>(
        'redmine.issues.assignedIds',
        { offset },
      ),

    /** One issue with its description, allowed status changes and Redmine history. */
    get: (
      issueId: number,
    ): Promise<{
      baseUrl: string | null;
      /** The caller's Redmine user id, to pick out their own activity. */
      me: number | null;
      /** The caller pinned this issue, so it is in their Tickets table. */
      pinned: boolean;
      /** The caller took this issue out of their Tickets table (bulk Delete). */
      removed: boolean;
      issue: RedmineIssueDetail;
      journals: RedmineJournal[];
      /**
       * The newest time logged on the issue in Redmine (up to 10), leaving out
       * the entries TimeHuddle pushed for the caller: those show as their own
       * sessions. `null` when Redmine would not list them.
       */
      timeEntries: RedmineTimeEntry[] | null;
    }> => wormholeCall('redmine.issues.get', { issueId }),

    /** Create an issue as the caller (authored under their own key). */
    create: (input: RedmineIssueCreateInput): Promise<RedmineIssueCreateResult> =>
      wormholeCall<RedmineIssueCreateResult>('redmine.issues.create', { ...input }),

    /**
     * Edit an issue as the caller. Refused with code `stale` if it changed in
     * Redmine after `expectedUpdatedAt` (the `updatedAt` the form opened with).
     */
    update: (
      issueId: number,
      expectedUpdatedAt: string,
      edits: RedmineIssueEdits,
    ): Promise<RedmineIssueWriteResult> =>
      wormholeCall<RedmineIssueWriteResult>('redmine.issues.update', {
        issueId,
        expectedUpdatedAt,
        edits: { ...edits },
      }),
  },

  prefs: {
    /**
     * Hide or clear one Redmine issue for the caller.
     *
     * Hiding affects that user's **suggestions only**: it changes nothing in
     * Redmine, nothing for other users, and nothing in search results or the
     * Tickets table. The server expires a dismissal after 15 days, and clears it
     * early if the issue becomes assigned to the user, so there is nothing for
     * the client to schedule. `null` is both Undo and Restore.
     *
     * One refusal beyond the usual Redmine ones: `too-many-requests` when they
     * are ahead of the rate limit, where the next attempt goes through.
     */
    set: (issueId: number, state: RedmineIssuePrefState): Promise<{ ok: true }> =>
      wormholeCall<{ ok: true }>('redmine.prefs.set', { issueId, state }),

    /** Issues the caller has hidden, newest first — the Restore list in Settings. */
    listDismissed: (): Promise<RedmineIssueList> =>
      wormholeCall<RedmineIssueList>('redmine.prefs.listDismissed', {}),
  },

  projects: {
    /** Projects the caller's key can see. */
    list: (): Promise<{ projects: RedmineNamed[] }> => wormholeCall('redmine.projects.list', {}),

    /** A project's trackers, assignable users and the instance's priorities. */
    formOptions: (projectId: number): Promise<RedmineFormOptions> =>
      wormholeCall<RedmineFormOptions>('redmine.projects.formOptions', { projectId }),
  },

  timeEntries: {
    /**
     * What a push would send. Read-only — creates nothing in Redmine. With
     * `only`, that one ticket-day: its hours and the activities its project allows.
     */
    preview: (only?: { ticketId: string; date: string }): Promise<RedmineTimeEntryPreview> =>
      wormholeCall<RedmineTimeEntryPreview>('redmine.timeEntries.preview', { ...only }),

    /**
     * Send the confirmed ticket-days to Redmine.
     *
     * **Irreversible** — entries cannot be edited or deleted afterwards.
     * Hours are recomputed server-side; only the selection and any activity
     * override travel from here.
     */
    push: (entries: RedmineTimeEntryPushRequest[]): Promise<RedmineTimeEntryPushResult> =>
      wormholeCall<RedmineTimeEntryPushResult>('redmine.timeEntries.push', { entries }),

    /**
     * Send one ticket-day's unsent time as a single entry, with an optional
     * comment: what the prompt shown when a ticket timer ends calls.
     *
     * **Irreversible**, like `push`, but allowed while clocked in. The hours
     * are worked out server-side, and so is the activity unless `activityId`
     * names another that the issue's project allows.
     */
    sendTicketDay: (
      ticketId: string,
      date: string,
      {
        comment,
        activityId,
        billable,
        hours,
      }: Omit<RedmineTimeEntryPushRequest, 'ticketId' | 'date'> = {},
    ): Promise<RedmineTicketDaySendOutcome> =>
      wormholeCall<RedmineTicketDaySendOutcome>('redmine.timeEntries.sendTicketDay', {
        ticketId,
        date,
        comment,
        activityId,
        billable,
        hours,
      }),

    /**
     * Never send one ticket-day's unsent time to Redmine. Nothing is written to
     * Redmine and the time stays in TimeHuddle; only time tracked on that day
     * later is offered again.
     */
    discard: (ticketId: string, date: string): Promise<{ discardedSeconds: number }> =>
      wormholeCall<{ discardedSeconds: number }>('redmine.timeEntries.discard', { ticketId, date }),
  },
};
